/* Ridge Studios preview stability fix. Replace the contents of assets/terrain-preview.js. */
(function() {
  // Detail colours are independent of Shopify's terrain/body variants.
  const DETAIL_COLOURS = {
    grey: {label:'Grey', hex:'#858585'},
    black: {label:'Black', hex:'#202020'},
    white: {label:'White', hex:'#f2f0e9'},
    green: {label:'Green', hex:'#52754b'},
    red: {label:'Red', hex:'#dc332b'},
    gold: {label:'Gold', hex:'#c68a36'},
    brown: {label:'Brown', hex:'#79523a'},
    blue: {label:'Blue', hex:'#3275b9'},
    beige: {label:'Beige', hex:'#d3bf9b'}
  };
  function detailColour(key) {
    return Object.prototype.hasOwnProperty.call(DETAIL_COLOURS,key) ? key : 'red';
  }
  function detailColourSelect(value, label, onChange) {
    const field=document.createElement('label');
    field.className='ridge-detail-colour-field';
    const swatch=document.createElement('span');
    swatch.className='ridge-detail-colour-swatch';
    swatch.setAttribute('aria-hidden','true');
    const select=document.createElement('select');
    select.className='ridge-detail-colour-select';
    select.setAttribute('aria-label',label);
    Object.entries(DETAIL_COLOURS).forEach(([key,colour]) => {
      const option=document.createElement('option');
      option.value=key; option.textContent=colour.label; select.appendChild(option);
    });
    select.value=detailColour(value);
    swatch.style.backgroundColor=DETAIL_COLOURS[select.value].hex;
    select.addEventListener('change',() => {
      swatch.style.backgroundColor=DETAIL_COLOURS[select.value].hex;
      onChange(select.value);
    });
    field.append(swatch,select);
    return field;
  }

  let threeLoadPromise = null,
    currentRenderer = null,
    currentAnimationFrame = null,
    currentMesh = null,
    currentScene = null,
    currentSourceGeometry = null,
    currentFrameRenderer = null,
    currentFrameAnimationFrame = null,
    currentFrameTerrainMesh = null,
    currentFrameMaterial = null,
    currentFrameAssemblyGroup = null,
    currentTerrainCleanup = null,
    currentFrameView = null,
    frameViewRequest = 0,
    selectedColour = "grey",
    selectedFrameColour = "black";
  const TERRAIN_COLOURS = {
      grey: {
        label: "Matte Grey",
        shopifyValue: "Matte Grey",
        model: 6842472,
        background: 12962769
      },
      white: {
        label: "Matte White",
        shopifyValue: "Matte White",
        model: 11974322,
        background: 11449788
      },
      black: {
        label: "Matte Black",
        shopifyValue: "Matte Black",
        model: 197379,
        background: 12962769
      },
      green: {
        label: "Matte Green",
        shopifyValue: "Matte Green",
        model: 1712397,
        swatch: 6845005,
        background: 12962769
      }
    },
    FRAME_COLOURS = {
      black: {
        label: "Black",
        shopifyValue: "Black",
        model: 1052688
      },
      white: {
        label: "White",
        shopifyValue: "White",
        model: 13158080
      },
      natural: {
        label: "Natural Oak",
        shopifyValue: "Natural Oak",
        model: 11967608
      }
    };

  function isFrameColourAllowed(terrain, key) {
    return terrain && normalise(terrain.size) === "large" && key === "natural" ? !1 : Object.prototype.hasOwnProperty.call(FRAME_COLOURS, key)
  }

  function ensureAllowedFrameColour(terrain) {
    return isFrameColourAllowed(terrain, selectedFrameColour) || (selectedFrameColour = "black"), selectedFrameColour
  }
  const FRAME_DIMENSIONS = {
    Mini: {
      width: 200,
      height: 150,
      rail: 10,
      depth: 30
    },
    Small: {
      width: 300,
      height: 210,
      rail: 16,
      depth: 30
    },
    Medium: {
      width: 400,
      height: 300,
      rail: 18,
      depth: 30
    },
    Large: {
      width: 520,
      height: 520,
      rail: 22,
      depth: 60
    }
  };

  function wait(ms) {
    return new Promise(function(resolve) {
      setTimeout(resolve, ms)
    })
  }

  function afterLayout(callback) {
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        callback()
      })
    })
  }

  function normalise(value) {
    return String(value ?? "").trim().toLowerCase()
  }

  function formatMultiplier(value) {
    const number = Number(value);
    return Number.isFinite(number) ? (Number.isInteger(number) ? number.toFixed(0) : number.toFixed(1)) + "\xD7" : "1.5\xD7"
  }

  function formatDistance(metres) {
    const value = Number(metres);
    return Number.isFinite(value) ? value >= 1e3 ? (value / 1e3).toFixed(2) + " km" : value.toFixed(0) + " m" : "\u2014"
  }

  function formatArea(squareMetres) {
    const value = Number(squareMetres);
    return Number.isFinite(value) ? value >= 1e6 ? (value / 1e6).toFixed(2) + " km\xB2" : (value / 1e4).toFixed(2) + " ha" : "\u2014"
  }

  function formatModelHeight(mm) {
    const value = Number(mm);
    return Number.isFinite(value) ? value.toFixed(1) + " mm" : "\u2014"
  }

  function formatOrientation(value) {
    const text = String(value || "landscape");
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  function getMountRotationDegrees(terrain) {
    const value = Number(terrain && terrain.mountRotationDegrees);
    return Number.isFinite(value) ? (Math.round(value / 90) * 90 % 360 + 360) % 360 : 0
  }

  function setMountRotationDegrees(terrain, value) {
    terrain && (terrain.mountRotationDegrees = (Math.round(Number(value) / 90) * 90 % 360 + 360) % 360)
  }

  function rotateTerrainBy90(terrain) {
    terrain && setMountRotationDegrees(terrain, getMountRotationDegrees(terrain) + 90)
  }

  function getMountRotationLabel(terrain) {
    return getMountRotationDegrees(terrain) + "\xB0"
  }

  // +Z appears at the bottom when the bird's-eye camera faces from +Z.
  // Rotate the WHOLE frame so the intended top rail starts at screen top,
  // independent of rotating only the terrain inside its frame later.
  function initialFrameYaw(terrain) {
    const size=normalise(terrain.size), orientation=normalise(terrain.orientation);
    if (orientation === 'landscape' && (size === 'mini' || size === 'small')) return Math.PI / 2;
    if (orientation === 'landscape' && size === 'medium') return -Math.PI / 2;
    return Math.PI;
  }

  function applyMountRotationToAssembly(terrain, assemblyGroup, terrainMesh) {
    terrainMesh && (assemblyGroup && (assemblyGroup.rotation.y = assemblyGroup.userData.ridgeInitialYaw || 0), terrainMesh.rotation.y = THREE.MathUtils.degToRad(getMountRotationDegrees(terrain)), terrainMesh.updateMatrixWorld(!0))
  }

  function applyMountRotationToCurrentMesh(terrain) {
    applyMountRotationToAssembly(terrain, currentFrameAssemblyGroup, currentFrameTerrainMesh)
  }

  function addFrameBackHardware(scene, terrain, frame) {
    if (!scene || !terrain || !frame) return;
    const size = normalise(terrain.size),
      metalMaterial = new THREE.MeshPhongMaterial({
        color: 11449786,
        specular: 16777215,
        shininess: 55
      }),
      darkMaterial = new THREE.MeshPhongMaterial({
        color: 3159096,
        specular: 2434341,
        shininess: 10
      }),
      holeMaterial = new THREE.MeshPhongMaterial({
        color: 2237993,
        specular: 526344,
        shininess: 4
      }),
      tabY = -4,
      raisedY = -9,
      metalThickness = 2,
      hookWidth = 15,
      endLength = 10;

    function createHook(totalLength, x, z, vertical) {
      const group = new THREE.Group,
        middleLength = Math.max(totalLength - endLength * 2, 1);

      function addFlatSegment(length, centreX, centreY) {
        const segment = new THREE.Mesh(new THREE.BoxGeometry(length, metalThickness, hookWidth), metalMaterial);
        segment.position.set(centreX, centreY, 0), group.add(segment)
      }
      const leftEndX = -(totalLength / 2 - endLength / 2),
        rightEndX = totalLength / 2 - endLength / 2;
      addFlatSegment(endLength, leftEndX, tabY), addFlatSegment(middleLength, 0, raisedY), addFlatSegment(endLength, rightEndX, tabY), [leftEndX, rightEndX].forEach(function(holeX) {
        [-3.2, 3.2].forEach(function(holeZ) {
          const hole = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 1.6, .7, 18), holeMaterial);
          hole.position.set(holeX, tabY - metalThickness / 2 - .35, holeZ), group.add(hole)
        })
      }), group.position.set(x, 0, z), vertical && (group.rotation.y = Math.PI / 2), scene.add(group)
    }

    function addSmallStand() {
      const hingeX = frame.width * .14,
        hingeZ = -frame.height * .02,
        hinge = new THREE.Mesh(new THREE.BoxGeometry(18, 3, 32), darkMaterial);
      hinge.position.set(hingeX, -4.5, hingeZ), scene.add(hinge);
      const armLength = Math.min(145, Math.min(frame.width, frame.height) * .68),
        arm = new THREE.Mesh(new THREE.BoxGeometry(9, 3, armLength), darkMaterial);
      arm.rotation.y = -Math.PI / 4;
      const halfRun = armLength / (2 * Math.sqrt(2));
      arm.position.set(hingeX + halfRun, -7, hingeZ - halfRun), scene.add(arm)
    }
    if (size === "mini") {
      // Hardware measured from the 130 x 180 mm backing, not the outer rails.
      // 20 mm is assumed to mean backing edge to nearest hook edge.
      const hardware = new THREE.Group();
      const originalScene = scene;
      scene = hardware;
      createHook(40, 0, 90 - 20 - hookWidth / 2, false);
      createHook(40, -(65 - 20 - hookWidth / 2), 0, true);
      // Folded stand is a visual estimate from the supplied rear photograph.
      const hingeX = -10, hingeZ = 20;
      const hinge = new THREE.Mesh(new THREE.BoxGeometry(20, 3, 12), darkMaterial);
      hinge.position.set(hingeX, -4.5, hingeZ);
      hardware.add(hinge);
      const armLength = 110;
      const armAngle = Math.PI / 6;
      const arm = new THREE.Mesh(new THREE.BoxGeometry(35, 2, armLength), darkMaterial);
      arm.rotation.y = -armAngle;
      arm.position.set(hingeX + armLength * Math.sin(armAngle) / 2, -7, hingeZ - armLength * Math.cos(armAngle) / 2);
      hardware.add(arm);
      scene = originalScene;
      if (terrain.orientation !== "portrait") hardware.rotation.y = Math.PI / 2;
      scene.add(hardware);
      return;
    }
    if (size === "small") {
      if (terrain.orientation === "portrait") {
        createHook(40, 0, frame.height / 2 - 45, !1), createHook(40, -(frame.width / 2 - 60), 0, !0), addSmallStand();
        return
      }
      createHook(40, frame.width / 2 - 45, 0, !0), createHook(40, 0, frame.height / 2 - 60, !1);
      const portraitWidth = 210,
        portraitHeight = 300,
        portraitHingeX = portraitWidth * .14,
        portraitHingeZ = -portraitHeight * .02,
        landscapeHingeX = portraitHingeZ,
        landscapeHingeZ = -portraitHingeX,
        landscapeHinge = new THREE.Mesh(new THREE.BoxGeometry(18, 3, 32), darkMaterial);
      landscapeHinge.position.set(landscapeHingeX, -4.5, landscapeHingeZ), scene.add(landscapeHinge);
      const armLength = Math.min(145, Math.min(portraitWidth, portraitHeight) * .68),
        halfRun = armLength / (2 * Math.sqrt(2)),
        portraitArmX = portraitHingeX + halfRun,
        landscapeArmX = portraitHingeZ - halfRun,
        landscapeArmZ = -portraitArmX,
        landscapeArm = new THREE.Mesh(new THREE.BoxGeometry(9, 3, armLength), darkMaterial);
      landscapeArm.rotation.y = Math.PI / 4, landscapeArm.position.set(landscapeArmX, -7, landscapeArmZ), scene.add(landscapeArm);
      return
    }
    if (size === "medium") {
      createHook(85, 0, frame.height / 2 - 55, !1), createHook(85, -(frame.width / 2 - 55), 0, !0);
      return
    }
    size === "large" && createHook(40, 0, frame.height / 2 - 50, !1)
  }

  function getCurrency() {
    return window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : "NZD"
  }

  function formatMoney(cents, includeCurrency) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return "\u2014";
    const dollars = value / 100;
    return "$" + (Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2)) + (includeCurrency ? " " + getCurrency() : "")
  }

  function getContainerSize(container) {
    const rect = container.getBoundingClientRect(),
      width = Math.max(Math.round(rect.width || container.clientWidth || 300), 300),
      height = Math.max(Math.round(rect.height || container.clientHeight || 300), 300);
    return {
      width,
      height
    }
  }

  function syncRendererSize(renderer, camera, container) {
    if (!renderer || !camera || !container) return;
    const size = getContainerSize(container);
    camera.aspect = size.width / size.height, camera.updateProjectionMatrix(), renderer.setSize(size.width, size.height, !1)
  }

  function loadThree() {
    if (window.THREE && window.THREE.OrbitControls && window.THREE.STLLoader) return Promise.resolve();
    if (threeLoadPromise) return threeLoadPromise;
    function loadScript(src, ready) {
      if (ready()) return Promise.resolve();
      return new Promise(function(resolve, reject) {
        let script = document.querySelector('script[src="' + src + '"]');
        if (script && script.dataset.ridgeFailed === "true") { script.remove(); script = null; }
        const isNew = !script;
        if (!script) { script = document.createElement("script"); script.src = src; }
        let settled = false;
        const timer = setTimeout(function() { finish(new Error("The 3D viewer took too long to load. Please retry.")); }, 25000);
        function finish(error) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          script.removeEventListener("load", loaded);
          script.removeEventListener("error", failed);
          if (error) {
            script.dataset.ridgeFailed = "true";
            script.remove();
            reject(error);
          } else { script.dataset.loaded = "true"; resolve(); }
        }
        function loaded() { finish(ready() ? null : new Error("The 3D viewer could not initialise. Please retry.")); }
        function failed() { finish(new Error("The 3D viewer could not download. Please retry.")); }
        script.addEventListener("load", loaded);
        script.addEventListener("error", failed);
        if (isNew) document.head.appendChild(script);
        else if (ready()) finish();
      });
    }
    threeLoadPromise = (async function() {
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await loadScript("https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js", function() { return !!window.THREE; });
          await loadScript("https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js", function() { return !!(window.THREE && window.THREE.OrbitControls); });
          await loadScript("https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/STLLoader.js", function() { return !!(window.THREE && window.THREE.STLLoader); });
          return;
        } catch (error) { lastError = error; if (!attempt) await wait(1000); }
      }
      throw lastError;
    })().catch(function(error) { threeLoadPromise = null; throw error; });
    return threeLoadPromise;
  }


  function getWrapper(terrain) {
    return terrain && terrain.sectionId ? document.getElementById("terrain-configurator-" + terrain.sectionId) : document.querySelector(".terrain-map-wrapper")
  }

  function getApiBase(wrapper) {
    const value = (wrapper.dataset.apiUrl || "").trim();
    if (!value || value.indexOf("your-terrain-api.example.com") !== -1) throw new Error("Terrain API URL has not been configured in the Shopify section settings.");
    return value.replace(/\/$/, "")
  }

  function getProductData(wrapper) {
    if (wrapper._ridgeTerrainProductData !== void 0) return wrapper._ridgeTerrainProductData;
    const element = wrapper.querySelector(".terrain-shopify-product-data");
    if (!element) return wrapper._ridgeTerrainProductData = null, null;
    try {
      const parsed = JSON.parse(element.textContent.trim());
      return wrapper._ridgeTerrainProductData = parsed, parsed
    } catch (error) {
      return console.error("Could not read Shopify terrain product data.", error), wrapper._ridgeTerrainProductData = null, null
    }
  }

  function getOptionIndex(productData, optionName) {
    if (!productData || !Array.isArray(productData.options)) return -1;
    const wanted = normalise(optionName);
    return productData.options.findIndex(function(option) {
      return normalise(option) === wanted
    })
  }

  function getVariantOption(variant, index) {
    return variant && Array.isArray(variant.options) ? variant.options[index] : variant ? variant["option" + (index + 1)] : null
  }

  function getSelectedShopifyValues(terrain) {
    return ensureAllowedFrameColour(terrain), {
      size: terrain.size,
      modelColour: TERRAIN_COLOURS[selectedColour].shopifyValue,
      frameFinish: FRAME_COLOURS[selectedFrameColour].shopifyValue
    }
  }

  function getTerrainMarkers(terrain) {
    return Array.isArray(terrain && terrain.markers)
      ? terrain.markers.slice(0, 10)
      : (terrain && terrain.marker ? [terrain.marker] : []);
  }

  function getGpxRoutes(terrain) {
    if(Array.isArray(terrain.gpxRoutes) && terrain.gpxRoutes.length) return terrain.gpxRoutes;
    return terrain.gpxSegments && terrain.gpxSegments.length
      ? [{id:'route_1',name:terrain.gpxName || 'GPX trail',colour:'red'}] : [];
  }
  function gpxSegmentColour(terrain,index) {
    const routes=getGpxRoutes(terrain);
    const id=(terrain.gpxSegmentRouteIds || [])[index];
    const route=routes.find(route => route.id===id) || routes[0];
    return detailColour(route && route.colour);
  }
  function detailColourProperties(terrain) {
    const properties={};
    getTerrainMarkers(terrain).forEach((marker,index) => {
      properties['Marker '+(index+1)+' colour']=DETAIL_COLOURS[detailColour(marker.colour)].label;
    });
    getGpxRoutes(terrain).forEach((route,index) => {
      properties['GPX '+(index+1)+' colour']=DETAIL_COLOURS[detailColour(route.colour)].label+' · '+route.name;
    });
    return properties;
  }

  function estimatedFinishedHeightMm(terrain) {
    const base = Number(terrain.finalModelHeightMm);
    return Number.isFinite(base) ? base + (getTerrainMarkers(terrain).length ? 2 : 0) + (terrain.gpxSegments && terrain.gpxSegments.length ? 1.3 : 0) : 0;
  }
  function isTallTerrain(terrain) { return estimatedFinishedHeightMm(terrain) > 100; }
  function getTallProductData(wrapper) {
    const script=wrapper.querySelector('.terrain-shopify-tall-product-data');
    if(!script) return null;
    try {return JSON.parse(script.textContent.trim());}catch(e){return null;}
  }
  function resolveSelectedVariant(wrapper, terrain) {
    const regular = getProductData(wrapper);
    const tall = isTallTerrain(terrain);
    const productData = tall ? getTallProductData(wrapper) : regular;
    if(tall && (!productData || !productData.variants)) {
      throw new Error('Terrains taller than 10 cm need the NZ$10 tall-terrain product configured. Please contact Ridge Studios.');
    }
    if (!productData || !Array.isArray(productData.variants)) throw new Error("Select your Terrain product in the Terrain Map section settings.");
    const sizeIndex = getOptionIndex(productData, "Size"),
      colourIndex = getOptionIndex(productData, "Model Colour"),
      frameIndex = getOptionIndex(productData, "Wood Frame Finish");
    if (sizeIndex === -1) throw new Error('The Shopify product needs an option called "Size".');
    if (colourIndex === -1) throw new Error('The Shopify product needs an option called "Model Colour".');
    if (frameIndex === -1) throw new Error('The Shopify product needs an option called "Wood Frame Finish".');
    const selections = getSelectedShopifyValues(terrain),
      variant = productData.variants.find(function(candidate) {
        const sizeMatches = normalise(getVariantOption(candidate, sizeIndex)) === normalise(selections.size),
          colourMatches = normalise(getVariantOption(candidate, colourIndex)) === normalise(selections.modelColour),
          frameMatches = normalise(getVariantOption(candidate, frameIndex)) === normalise(selections.frameFinish);
        return sizeMatches && colourMatches && frameMatches
      });
    if (!variant) throw new Error("No Shopify variant exists for " + selections.size + " + " + selections.modelColour + " + " + selections.frameFinish + ".");
    if(tall) {
      const regularVariant=regular && regular.variants.find(function(candidate){
        const si=getOptionIndex(regular,'Size'),ci=getOptionIndex(regular,'Model Colour'),fi=getOptionIndex(regular,'Wood Frame Finish');
        return normalise(getVariantOption(candidate,si))===normalise(selections.size) &&
          normalise(getVariantOption(candidate,ci))===normalise(selections.modelColour) &&
          normalise(getVariantOption(candidate,fi))===normalise(selections.frameFinish);
      });
      const shopCurrency=(window.Shopify && window.Shopify.currency && window.Shopify.currency.active)||'NZD';
      const difference=Number(variant.price)-Number(regularVariant && regularVariant.price);
      if(!regularVariant || (shopCurrency==='NZD' ? difference!==1000 : difference<=0)) {
        throw new Error('Tall terrain price is misconfigured. It must be NZ$10 higher than the regular option (converted for international markets).');
      }
    }
    return terrain.variantId = Number(variant.id), terrain.variantPriceCents = Number(variant.price), terrain.price = Number(variant.price) / 100, variant
  }

  // Each charge is a genuine Shopify line item, never an editable cart-property price.
  const RIDGE_EXTRA_AMOUNTS_NZD = { pin:500, gpx:1000 };
  function selectedTerrainExtras(terrain) {
    return { pin: getTerrainMarkers(terrain).length > 0,
      gpx: !!(terrain.gpxSegments && terrain.gpxSegments.length) };
  }
  function resolveTerrainAddonVariants(wrapper, terrain) {
    const requested = selectedTerrainExtras(terrain);
    if (!requested.pin && !requested.gpx) return [];
    const script = wrapper.querySelector('.terrain-shopify-addon-product-data');
    let product;
    try { product = script && JSON.parse(script.textContent.trim()); } catch (e) { product=null; }
    if (!product || !Array.isArray(product.variants))
      throw new Error('Terrain customisation pricing is not configured. Please contact Ridge Studios.');
    const shopCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'NZD';
    return [['pin','Location Pin'], ['gpx','GPX Trail']].filter(([key]) => requested[key]).map(([key,label]) => {
      const variant = product.variants.find(candidate => {
        const option = getVariantOption(candidate, getOptionIndex(product,'Extra'));
        return normalise(option || candidate.title) === normalise(label);
      });
      if (!variant || !variant.id || variant.available === false)
        throw new Error(label + ' Shopify add-on is unavailable. Please contact Ridge Studios.');
      const price = Number(variant.price);
      if (!Number.isFinite(price) || price <= 0 || (shopCurrency === 'NZD' && price !== RIDGE_EXTRA_AMOUNTS_NZD[key]))
        throw new Error(label + ' must cost exactly NZ$' + (RIDGE_EXTRA_AMOUNTS_NZD[key]/100).toFixed(2) + '. Check the Shopify variant pricing.');
      return {key, label, id:Number(variant.id), price};
    });
  }

  function updateSizeCardPrices(wrapper) {
    const productData = getProductData(wrapper);
    if (!productData || !Array.isArray(productData.variants)) return;
    const sizeIndex = getOptionIndex(productData, "Size"),
      colourIndex = getOptionIndex(productData, "Model Colour"),
      frameIndex = getOptionIndex(productData, "Wood Frame Finish");
    if (sizeIndex === -1 || colourIndex === -1 || frameIndex === -1) return;
    const allowedColours = Object.keys(TERRAIN_COLOURS).map(function(key) {
        return normalise(TERRAIN_COLOURS[key].shopifyValue)
      }),
      allowedFrames = Object.keys(FRAME_COLOURS).map(function(key) {
        return normalise(FRAME_COLOURS[key].shopifyValue)
      });
    wrapper.querySelectorAll(".terrain-size-option").forEach(function(button) {
      const size = button.dataset.size,
        matching = productData.variants.filter(function(variant) {
          return normalise(getVariantOption(variant, sizeIndex)) === normalise(size) && allowedColours.includes(normalise(getVariantOption(variant, colourIndex))) && allowedFrames.includes(normalise(getVariantOption(variant, frameIndex))) && !(normalise(size) === "large" && normalise(getVariantOption(variant, frameIndex)) === normalise(FRAME_COLOURS.natural.shopifyValue))
        });
      if (!matching.length) return;
      const prices = matching.map(function(variant) {
        return Number(variant.price)
      }).filter(function(price) {
        return Number.isFinite(price)
      });
      if (!prices.length) return;
      const minimum = Math.min.apply(Math, prices),
        maximum = Math.max.apply(Math, prices);
      button.dataset.price = String(minimum / 100);
      const output = button.querySelector(".terrain-price");
      output && (output.textContent = minimum === maximum ? formatMoney(minimum, !1) : "From " + formatMoney(minimum, !1))
    })
  }

  function getFrameDimensions(terrain) {
    const base = FRAME_DIMENSIONS[terrain.size];
    if (!base) throw new Error("Unknown frame size.");
    let width = base.width,
      height = base.height;
    if (terrain.orientation === "portrait" && width !== height) {
      const oldWidth = width;
      width = height, height = oldWidth
    }
    return {
      width,
      height,
      rail: base.rail,
      depth: base.depth
    }
  }

  function addStyles() {
    if (document.getElementById("ridge-terrain-preview-styles")) return;
    const style = document.createElement("style");
    style.id = "ridge-terrain-preview-styles", style.textContent = `

.ridge-detail-colour-field{display:inline-flex;align-items:center;gap:7px;min-width:0;max-width:100%;color:#263d30;}
.ridge-detail-colour-swatch{display:inline-block;flex:none;width:17px;height:17px;border:1px solid #888;border-radius:50%;}
.ridge-detail-colour-select{box-sizing:border-box;max-width:100%;min-width:95px;min-height:40px;padding:7px 22px 7px 8px;border:1px solid #b5c4b7;border-radius:6px;background:#fff;color:#243b2c;font:inherit;font-size:16px;cursor:pointer;color-scheme:light;}
.ridge-detail-colour-select:focus-visible{outline:2px solid #3c6c4c;outline-offset:2px;}
.ridge-detail-colour-row{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px;font-size:13px;color:#263d30;}
.ridge-detail-colour-name{min-width:0;overflow-wrap:anywhere;flex:1 1 110px;}

.ridge-customisation-controls > .ridge-customisation-block:only-child {grid-column:1 / -1;}
.ridge-customisation-controls {display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:18px 0 22px;font-family:var(--font-body-family,inherit);}
.ridge-customisation-block {padding:19px;border:1px solid #dededc;border-radius:12px;background:#fbfbfa;color:#25392b;min-width:0;}
.ridge-customisation-heading {display:flex;align-items:flex-start;gap:12px;}
.ridge-customisation-heading strong {font-family:var(--font-heading-family,inherit);font-size:16px;font-weight:650;color:#202823;}
.ridge-customisation-heading p,.ridge-customisation-hint {font-size:13px;line-height:1.5;color:#69726b;margin:5px 0 0;}
.ridge-customisation-symbol {display:inline-flex;align-items:center;justify-content:center;flex:none;width:30px;height:30px;border-radius:8px;background:#e8eee8;color:#a93a32;font-size:15px;font-weight:800;}
.ridge-customisation-price {font-family:var(--font-body-family,inherit);font-size:12px;font-weight:600;color:#45564b;}
.ridge-gpx-input-row {display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:15px;}
.ridge-gpx-upload-label {display:inline-flex;justify-content:center;align-items:center;padding:10px 15px;border:1px solid #293e30;border-radius:7px;background:#253c2c;color:white;cursor:pointer;font:600 13px var(--font-body-family,inherit);}
.ridge-gpx-upload-label:hover {background:#375843;}
.ridge-gpx-input {position:absolute;width:1px;height:1px;clip-path:inset(50%);overflow:hidden;}
.ridge-gpx-upload-label:focus-within {outline:2px solid #437d55;outline-offset:3px;}
.ridge-gpx-remove {border:1px solid #b9c3ba;background:#fff;border-radius:7px;padding:10px 14px;font:600 13px var(--font-body-family,inherit);color:#263c2c;cursor:pointer;}
.ridge-gpx-remove[hidden] {display:none!important;}
.ridge-marker-state,.ridge-gpx-state {font-size:14px;line-height:1.5;color:#263d30;}
.ridge-gpx-state {margin:12px 0 0;color:#536055;overflow-wrap:anywhere;}
.ridge-customisation-controls button,.ridge-customisation-controls input {font-family:var(--font-body-family,inherit);}
@media(max-width:749px){.ridge-customisation-controls{grid-template-columns:1fr;}}
.terrain-map-wrapper {font-family:var(--font-body-family,inherit);}
.terrain-map-wrapper h2,.terrain-map-wrapper h3 {font-family:var(--font-heading-family,inherit);}
.terrain-map-wrapper button {font-family:var(--font-body-family,inherit);}
.terrain-preview-disclaimer {
margin: -4px 0 14px;
padding: 10px 14px;

border-left: 3px solid #b8b8b8;

background: #fafafa;

color: #666;

font-size: 13px;
line-height: 1.5;

}

.terrain-preview-disclaimer strong {
color: #333;
margin-right: 4px;
}

.terrain-preview-disclaimer span {
color: #666;
}

.terrain-generation-progress {

width: 88%;
max-width: 520px;
margin: 0 auto;
text-align: center;

}

.terrain-generation-progress-title {
margin: 0 0 8px;
font-size: 18px;
font-weight: 600;
color: #222;
}

.terrain-generation-time-note {
margin: -2px 0 14px;
font-size: 13px;
line-height: 1.4;
color: #777;
}

.terrain-generation-progress-status {
min-height: 42px;
margin: 0 0 18px;
font-size: 14px;
color: #666;
}

.terrain-generation-progress-track {
position: relative !important;
display: block !important;
width: 100% !important;
height: 14px !important;
overflow: hidden !important;
border-radius: 999px !important;
background: #d1d1d1 !important;
}

.terrain-generation-progress-bar {
position: absolute !important;
inset: 0 !important;
width: 100% !important;
height: 100% !important;
background: #222 !important;
transform-origin: left center !important;
transform: scaleX(0);
transition: transform .35s ease !important;
}

.terrain-generation-progress-bottom {
display: flex;
justify-content: space-between;
gap: 12px;
margin-top: 9px;
font-size: 12px;
color: #777;
}

.terrain-generation-progress-percent {
font-weight: 700;
color: #333;
}

.terrain-generation-progress-eta {
margin: 8px 0 0;
min-height: 18px;
font-size: 13px;
font-weight: 600;
color: #444;
}

.terrain-colour-controls,
.terrain-frame-colour-controls {
margin: 18px 0 14px;
display: flex;
align-items: center;
justify-content: space-between;
gap: 15px;
flex-wrap: wrap;
}

.terrain-colour-buttons,
.terrain-frame-colour-buttons {
display: flex;
gap: 8px;
flex-wrap: wrap;
}

.terrain-mount-orientation-controls {
margin: 14px 0 18px;
padding: 14px;
border: 1px solid #ddd;
border-radius: 6px;
background: #f7f7f7;
}

.terrain-mount-orientation-title {
display: block;
margin-bottom: 9px;
font-size: 13px;
color: #666;
}

.terrain-mount-orientation-buttons {
display: flex;
gap: 8px;
flex-wrap: wrap;
}

.terrain-mount-orientation-button {
min-height: 42px;
padding: 9px 14px;
background: #fff;
color: #111;
border: 1px solid #c8c8c8;
border-radius: 6px;
cursor: pointer;
}

.terrain-mount-orientation-button.selected {
border: 2px solid #111;
padding: 8px 13px;
font-weight: 600;
}

.terrain-mount-orientation-help {
display: block;
margin-top: 9px;
font-size: 12px;
line-height: 1.4;
color: #707070;
}

.terrain-viewer-help {
display: flex;
align-items: flex-start;
gap: 10px;
margin: 12px 0 14px;
padding: 12px 14px;
border: 1px solid #dedede;
border-radius: 6px;
background: #f7f7f7;
color: #555;
font-size: 14px;
line-height: 1.5;
}

.terrain-viewer-help strong {
flex: 0 0 auto;
color: #111;
}

.terrain-viewer-help span {
color: #555;
}

.terrain-colour-button,
.terrain-frame-colour-button {
min-width: 110px;
min-height: 42px;
padding: 9px 14px;
background: #fff;
color: #111;
border: 1px solid #c8c8c8;
border-radius: 6px;
cursor: pointer;
display: flex;
align-items: center;
justify-content: center;
gap: 8px;
}

.terrain-colour-button.selected,
.terrain-frame-colour-button.selected {
border: 2px solid #111;
padding: 8px 13px;
}

.terrain-colour-swatch,
.terrain-frame-colour-swatch {
width: 16px;
height: 16px;
border-radius: 50%;
border: 1px solid rgba(0,0,0,.30);
}

.terrain-frame-section,
.terrain-review-section {
margin-top: 34px;
padding-top: 30px;
border-top: 1px solid #ddd;
}

.terrain-frame-summary {
display: grid;
grid-template-columns: repeat(3,1fr);
gap: 10px;
margin: 18px 0;
}

.terrain-frame-summary-item {
padding: 14px;
border: 1px solid #ddd;
border-radius: 6px;
background: #f7f7f7;
color: #263d30;
}

.terrain-frame-summary-item span {
display: block;
margin-bottom: 4px;
font-size: 12px;
color: #666;
}

.terrain-frame-summary-item strong {
display: block;
font-size: 15px;
color: #20372a;
}

.terrain-frame-canvas,
.terrain-review-canvas {
position: relative !important;
display: block !important;
width: 100% !important;
height: 560px !important;
min-height: 560px !important;
overflow: hidden !important;
border-radius: 8px !important;
background: #b8bec4 !important;
}

.terrain-frame-canvas canvas,
.terrain-review-canvas canvas {
position: absolute !important;
inset: 0 !important;
display: block !important;
width: 100% !important;
height: 100% !important;
max-width: none !important;
max-height: none !important;
}

.terrain-frame-actions {
display: flex;
gap: 15px;
margin-top: 15px;
}

.terrain-frame-back,
.terrain-frame-confirm {
padding: 14px 28px;
border-radius: 5px;
cursor: pointer;
font-size: 15px;
}

.terrain-frame-back {
background: #fff;
color: #111;
border: 1px solid #ccc;
}

.terrain-frame-confirm {
flex: 1;
border: none;
background: #111;
color: #fff;
}

.terrain-review-layout {
display: grid;
grid-template-columns:
minmax(0,1.2fr)
minmax(320px,.8fr);
gap: 22px;
align-items: start;
}

.terrain-review-card {
padding: 22px;
border: 1px solid #ddd;
border-radius: 8px;
background: #fff;
}

.terrain-review-product {
margin-bottom: 18px;
padding-bottom: 18px;
border-bottom: 1px solid #e3e3e3;
}

.terrain-review-product-name {
margin-bottom: 5px;
font-size: 22px;
font-weight: 650;
}

.terrain-review-price {
font-size: 20px;
font-weight: 700;
}

.terrain-review-row {
display: flex;
justify-content: space-between;
gap: 20px;
padding: 10px 0;
border-bottom: 1px solid #eee;
font-size: 14px;
}

.terrain-review-row span {
color: #666;
}

.terrain-review-row strong {
text-align: right;
}

.terrain-review-cart-error {
display: none;
margin-top: 14px;
padding: 12px 14px;
border: 1px solid #e8caca;
border-radius: 6px;
background: #fff4f4;
color: #8a2525;
}

.terrain-review-actions {
display: flex;
gap: 10px;
margin-top: 20px;
}

.terrain-review-edit,
.terrain-add-to-cart {
min-height: 50px;
padding: 13px 18px;
border-radius: 5px;
cursor: pointer;
}

.terrain-review-edit {
border: 1px solid #ccc;
background: #fff;
}

.terrain-add-to-cart {
flex: 1;
border: none;
background: #111;
color: #fff;
font-weight: 600;
}

.terrain-add-to-cart:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.terrain-add-to-cart:not(:disabled) {
  opacity: 1;
  cursor: pointer;
}

@media screen and (max-width:749px) {

.terrain-viewer-help {
display: block;
font-size: 13px;
}

.terrain-viewer-help strong {
display: block;
margin-bottom: 4px;
}



.terrain-colour-controls,
.terrain-frame-colour-controls {
flex-direction: column;
align-items: flex-start;
}

.terrain-mount-orientation-buttons {
display: grid;
grid-template-columns: 1fr;
width: 100%;
}

.terrain-mount-orientation-button {
width: 100%;
}

.terrain-frame-summary {
grid-template-columns: 1fr 1fr;
}

.terrain-frame-canvas,
.terrain-review-canvas {
height: 390px !important;
min-height: 390px !important;
}

.terrain-frame-actions,
.terrain-review-actions {
flex-direction: column;
}

.terrain-review-layout {
grid-template-columns: 1fr;
}

}

`, document.head.appendChild(style)
  }

  function createProgressUI(loadingElement) {
    addStyles(), loadingElement.innerHTML = `

<div class="terrain-generation-progress">

<p class="terrain-generation-progress-title">
  Creating your terrain
</p>

<p class="terrain-generation-time-note">
  Please allow up to 3–4 minutes for larger or more detailed areas.
</p>

<p class="terrain-generation-progress-status">
  Starting terrain generation...
</p>

<div class="terrain-generation-progress-track">
  <div class="terrain-generation-progress-bar"></div>
</div>

<div class="terrain-generation-progress-bottom">

  <span>
    Accurate elevation model
  </span>

  <span class="terrain-generation-progress-percent">
    0%
  </span>

</div>

<p class="terrain-generation-progress-eta">
  Estimating time...
</p>

</div>

`
  }

  function renderAnonymousQueue(loadingElement, data) {
    if (!loadingElement || !data || !Array.isArray(data.active)) return;
    let holder = loadingElement.querySelector('.ridge-anonymous-queue');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'ridge-anonymous-queue';
      holder.style.cssText = 'background:#fff;border:1px solid #ddd;border-radius:12px;margin:12px auto;padding:12px;text-align:left;max-width:520px;width:88%';
      loadingElement.querySelector('.terrain-generation-progress').appendChild(holder);
    }
    holder.replaceChildren();
    const title = document.createElement('strong');
    title.textContent = 'Generation Queue';
    holder.appendChild(title);
    data.active.slice(0, 6).forEach(function(job, index) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;gap:8px;border-top:1px solid #eee;padding:7px 0;font-size:12px';
      const label = document.createElement('span');
      label.textContent = 'Job ' + (index + 1) + ' — ' + (job.type === 'Paid STL' ? 'Paid STL' : 'Lightweight Preview');
      const value = document.createElement('span');
      value.textContent = job.status === 'processing' ? 'Generating ' + Math.min(100,Math.max(0,Number(job.progress)||0)) + '%' : 'Waiting · Position ' + Number(job.position || 1);
      row.append(label, value); holder.appendChild(row);
    });
  }

  function formatTerrainEta(seconds, isQueued) {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value < 0) return "Estimating time...";

    if (!isQueued && value <= 15) return "Almost ready...";

    if (value < 60) {
      const roundedSeconds = Math.max(10, Math.round(value / 10) * 10);
      return (isQueued ? "Estimated total time: ~" : "Estimated time remaining: ~") + roundedSeconds + " sec";
    }

    const roundedMinutes = Math.max(1, Math.round(value / 30) / 2);
    const minutesText = Number.isInteger(roundedMinutes) ? roundedMinutes.toFixed(0) : roundedMinutes.toFixed(1);
    return (isQueued ? "Estimated total time: ~" : "Estimated time remaining: ~") + minutesText + " min";
  }

  function updateProgress(loadingElement, percent, message, etaSeconds, isQueued) {
    const safePercent = Math.max(0, Math.min(100, Math.round(Number(percent) || 0))),
      bar = loadingElement.querySelector(".terrain-generation-progress-bar"),
      status = loadingElement.querySelector(".terrain-generation-progress-status"),
      output = loadingElement.querySelector(".terrain-generation-progress-percent"),
      eta = loadingElement.querySelector(".terrain-generation-progress-eta");
    bar && bar.style.setProperty("transform", "scaleX(" + safePercent / 100 + ")", "important");
    status && message && (status.textContent = message);
    output && (output.textContent = safePercent + "%");
    eta && etaSeconds !== undefined && etaSeconds !== null && (eta.textContent = formatTerrainEta(etaSeconds, !!isQueued));
  }
  const pendingTerrainJobs = new WeakMap();
  const MAX_PREVIEW_BYTES = 12 * 1024 * 1024;

  function terrainRequestData(terrain) {
    return {
      north: terrain.north, south: terrain.south, east: terrain.east, west: terrain.west,
      size: terrain.size, orientation: terrain.orientation, height_multiplier: terrain.heightMultiplier,
      rotation_degrees: Number(terrain.rotationDegrees || terrain.rotation_degrees || 0),
      corners: Array.isArray(terrain.corners) && terrain.corners.length === 4 ? terrain.corners : null
    };
  }

  async function terrainJson(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(function() { controller.abort(); }, 30000);
    try {
      const response = await fetch(url, Object.assign({}, options || {}, {signal: controller.signal}));
      let data;
      try { data = await response.json(); } catch (error) {
        const invalid = new Error("The terrain service returned an incomplete response. Please retry.");
        invalid.status = response.status;
        invalid.retryable = response.ok || response.status >= 500;
        throw invalid;
      }
      if (!response.ok) {
        const error = new Error(typeof data.detail === "string" ? data.detail : "The terrain service is temporarily unavailable. Please retry.");
        error.status = response.status;
        error.retryable = response.status >= 500 || response.status === 429 || response.status === 408;
        throw error;
      }
      return data;
    } catch (error) {
      if (controller.signal.aborted) {
        const timeout = new Error("The connection to the terrain service timed out. Please retry.");
        timeout.retryable = true;
        throw timeout;
      }
      if (error instanceof TypeError) error.retryable = true;
      throw error;
    } finally { clearTimeout(timer); }
  }

  async function startTerrainJob(apiBase, terrain) {
    // Do not automatically repeat POST: the first request may have queued a job.
    const data = await terrainJson(apiBase + "/terrain/start", {
      method: "POST", headers: {"Content-Type": "application/json", Accept: "application/json"},
      body: JSON.stringify(terrainRequestData(terrain))
    });
    if (!data || !/^[a-f0-9]{12}$/.test(data.job_id || "")) throw new Error("The terrain service did not return a valid job ID.");
    return data.job_id;
  }

  async function waitForTerrainJob(apiBase, jobId, loadingElement) {
    const startedAt = Date.now();
    let lastQueueRefresh = 0;
    let failures = 0, lastProgress = 1, displayedEta = null, lastEtaTick = Date.now();
    for (;;) {
      if (Date.now() - startedAt > 20 * 60 * 1000) throw new Error("Your terrain is taking longer than usual. Retry to check the same job.");
      let data;
      try {
        data = await terrainJson(apiBase + "/terrain/status/" + encodeURIComponent(jobId), {cache: "no-store"});
        failures = 0;
      } catch (error) {
        if (error.status === 404) error.forgetTerrainJob = true;
        if (!error.retryable || ++failures > 4) throw error;
        updateProgress(loadingElement, lastProgress, "Connection interrupted. Reconnecting to your terrain...", displayedEta);
        await wait(Math.min(5000, 1000 * failures));
        continue;
      }

      lastProgress = Math.max(lastProgress, Math.min(94, Number(data.progress) || 0));

      const serverEta = Number(data.estimated_seconds_remaining);
      const etaNow = Date.now();

      // Make the customer-facing ETA count down naturally between polls.
      // Never let a late backend stage make the visible ETA jump upward.
      if (displayedEta !== null) {
        displayedEta = Math.max(0, displayedEta - (etaNow - lastEtaTick) / 1000);
      }
      lastEtaTick = etaNow;

      if (Number.isFinite(serverEta) && serverEta >= 0) {
        if (displayedEta === null) {
          displayedEta = serverEta;
        } else if (serverEta < displayedEta) {
          // Follow faster-than-expected progress smoothly.
          displayedEta = displayedEta * 0.65 + serverEta * 0.35;
        }
        // If serverEta is higher, keep counting down instead of jumping up.
        // A genuinely slow final stage will simply show "Almost ready...".
      }

      if (Date.now() - lastQueueRefresh > 4000) {
        lastQueueRefresh = Date.now();
        terrainJson(apiBase + "/terrain/queue", {cache: "no-store"})
          .then(data => renderAnonymousQueue(loadingElement, data))
          .catch(() => {});
      }
      updateProgress(
        loadingElement,
        lastProgress,
        data.message || "Generating terrain...",
        displayedEta,
        data.status === "queued"
      );

      if (data.status === "ready") return data.result || {};
      if (data.status === "error") {
        const error = new Error(data.error || data.message || "Terrain generation failed.");
        error.forgetTerrainJob = true;
        throw error;
      }
      await wait(1000);
    }
  }

  function downloadPreview(url, loadingElement, expectedBytes) {
    return new Promise(function(resolve, reject) {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let idleTimer;
      function fail(message, retryable, status) {
        if (settled) return;
        settled = true;
        clearTimeout(idleTimer);
        const error = new Error(message);
        error.retryable = !!retryable;
        error.status = status;
        if (status === 404) error.forgetTerrainJob = true;
        reject(error);
      }
      function resetIdle() {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function() {
          fail("The preview download stopped responding. Please retry.", true);
          xhr.abort();
        }, 25000);
      }
      xhr.open("GET", url, true);
      xhr.responseType = "arraybuffer";
      xhr.timeout = 90000;
      xhr.onprogress = function(event) {
        resetIdle();
        const total = event.lengthComputable ? event.total : Number(expectedBytes) || 0;
        if (event.loaded > MAX_PREVIEW_BYTES || total > MAX_PREVIEW_BYTES) {
          fail("The preview is too large. Please contact us so we can prepare your terrain.", false);
          xhr.abort();
          return;
        }
        const percent = total ? 95 + Math.min(3, 3 * event.loaded / total) : 95;
        const detail = total ? Math.round(100 * event.loaded / total) + "%" : (event.loaded / 1048576).toFixed(1) + " MB";
        updateProgress(loadingElement, percent, "Downloading 3D preview... " + detail, 10);
      };
      xhr.onload = function() {
        if (settled) return;
        clearTimeout(idleTimer);
        if (xhr.status < 200 || xhr.status >= 300) {
          fail(xhr.status === 404 ? "This preview has expired. Please generate it again." : "The preview could not download. Please retry.",
               xhr.status >= 500 || xhr.status === 429 || xhr.status === 408, xhr.status);
          return;
        }
        const buffer = xhr.response;
        if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 134 || buffer.byteLength > MAX_PREVIEW_BYTES) {
          fail("The preview file is incomplete or too large. Please retry.", false); return;
        }
        const count = new DataView(buffer).getUint32(80, true);
        if (!count || buffer.byteLength !== 84 + count * 50) {
          fail("The preview download was incomplete. Please retry.", true); return;
        }
        settled = true;
        resolve(buffer);
      };
      xhr.onerror = function() { fail("The preview connection was interrupted. Please retry.", true); };
      xhr.ontimeout = function() { fail("The preview download timed out. Please retry.", true); };
      xhr.onabort = function() { fail("The preview download was interrupted. Please retry.", true); };
      resetIdle();
      xhr.send();
    });
  }

  async function loadStlGeometry(url, loadingElement, expectedBytes) {
    let buffer;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        updateProgress(loadingElement, 95, attempt ? "Reconnecting to your completed preview..." : "Downloading 3D preview...", 10);
        buffer = await downloadPreview(url, loadingElement, expectedBytes);
        break;
      } catch (error) {
        if (!error.retryable || attempt === 2) throw error;
        await wait(1000 * (attempt + 1));
      }
    }
    updateProgress(loadingElement, 99, "Preparing interactive 3D preview...", 5);
    await wait(0); // Let the browser paint before parsing the bounded-size mesh.
    let geometry;
    try {
      geometry = new THREE.STLLoader().parse(buffer);
      geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      if (!box || ![box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z].every(Number.isFinite)) {
        throw new Error("The preview contains invalid coordinates. Please retry.");
      }
      return geometry;
    } catch (error) { if (geometry) geometry.dispose(); throw error; }
  }


  function createColourControls(previewSection) {
    let controls = previewSection.querySelector(".terrain-colour-controls");
    if (controls) return;
    controls = document.createElement("div"), controls.className = "terrain-colour-controls", controls.innerHTML = `

<span class="terrain-colour-title"> Model colour </span>

<div class="terrain-colour-buttons"></div>

`;
    const buttons = controls.querySelector(".terrain-colour-buttons");
    Object.keys(TERRAIN_COLOURS).forEach(function(key) {
      const setting = TERRAIN_COLOURS[key],
        button = document.createElement("button");
      button.type = "button", button.className = "terrain-colour-button", button.dataset.colour = key, button.innerHTML = '<span class="terrain-colour-swatch" style="background:#' + (setting.swatch || setting.model).toString(16).padStart(6, "0") + '"></span><span>' + setting.label + "</span>", button.addEventListener("click", function() {
        selectedColour = key, window.selectedTerrain && (window.selectedTerrain.colour = key), applyTerrainColour(key), updateColourButtons(controls)
      }), buttons.appendChild(button)
    });
    const canvas = previewSection.querySelector(".terrain-preview-canvas, .terrain-frame-canvas");
    previewSection.insertBefore(controls, canvas), updateColourButtons(controls)
  }

  function updateColourButtons(controls) {
    controls.querySelectorAll(".terrain-colour-button").forEach(function(button) {
      button.classList.toggle("selected", button.dataset.colour === selectedColour)
    })
  }

  function applyTerrainColour(key) {
    const setting = TERRAIN_COLOURS[key];
    setting && ([currentMesh, currentFrameTerrainMesh].forEach(function(mesh) {
      !mesh || !mesh.material || (mesh.material.color.setHex(setting.model), mesh.material.specular && mesh.material.specular.setHex(key === "black" ? 3947580 : 2434341), mesh.material.needsUpdate = !0)
    }), currentScene && (currentScene.background = new THREE.Color(setting.background)), updateFrameSummary())
  }

  // Three.js does not dispose scene resources when a canvas is removed.
  function disposeSceneResources(scene) {
    if (!scene) return;
    const geometries = new Set(),
      materials = new Set(),
      textures = new Set();
    scene.traverse(function(object) {
      if (object.geometry) geometries.add(object.geometry);
      const list = Array.isArray(object.material) ? object.material : [object.material];
      list.forEach(function(material) {
        if (!material) return;
        materials.add(material);
        Object.keys(material).forEach(function(key) {
          if (material[key] && material[key].isTexture) textures.add(material[key]);
        });
      });
    });
    geometries.forEach(function(geometry) {
      geometry.dispose();
    });
    materials.forEach(function(material) {
      material.dispose();
    });
    textures.forEach(function(texture) {
      texture.dispose();
    });
  }

  function releasePreviewRenderer(renderer) {
    if (!renderer) return;
    if (renderer.renderLists) renderer.renderLists.dispose();
    renderer.dispose();
    if (renderer.forceContextLoss) renderer.forceContextLoss();
    renderer.domElement.remove();
    renderer.domElement.width = 1;
    renderer.domElement.height = 1;
  }

  function previewPixelRatio() {
    const compact = window.matchMedia && window.matchMedia("(max-width: 749px), (pointer: coarse)").matches;
    return Math.min(window.devicePixelRatio || 1, compact ? 1 : 2);
  }

  function previewIsVisible(container) {
    if (document.hidden || !container || !container.isConnected) return false;
    const rect = container.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function disposeTerrainPreview() {
    if (currentAnimationFrame !== null) cancelAnimationFrame(currentAnimationFrame);
    currentAnimationFrame = null;
    if (currentTerrainCleanup) currentTerrainCleanup();
    currentTerrainCleanup = null;
    disposeSceneResources(currentScene);
    releasePreviewRenderer(currentRenderer);
    currentRenderer = currentMesh = currentScene = null;
  }

  function createPreview(container, sourceGeometry, terrain) {
    frameViewRequest++;
    disposeTerrainPreview();
    disposeFramePreview();
    if (currentSourceGeometry && currentSourceGeometry !== sourceGeometry) currentSourceGeometry.dispose();
    // Keep the loaded STL as the immutable source instead of making another full copy.
    currentSourceGeometry = sourceGeometry;
    container.innerHTML = "", sourceGeometry.computeBoundingBox();
    const physicalSize = new THREE.Vector3;
    sourceGeometry.boundingBox.getSize(physicalSize), terrain.finalModelHeightMm = physicalSize.z, terrain.generatedWidthMm = physicalSize.x, terrain.generatedHeightMm = physicalSize.y, window.selectedTerrain = terrain;
    const geometry = sourceGeometry.clone(),
      size = getContainerSize(container);
    geometry.computeBoundingBox(), geometry.computeVertexNormals();
    const originalSize = new THREE.Vector3;
    geometry.boundingBox.getSize(originalSize);
    const scale = 10 / Math.max(originalSize.x, originalSize.y, 1);
    geometry.scale(scale, scale, scale), geometry.rotateX(-Math.PI / 2), geometry.computeBoundingBox();
    const box = geometry.boundingBox,
      centre = new THREE.Vector3;
    box.getCenter(centre), geometry.translate(-centre.x, -box.min.y, -centre.z), geometry.computeBoundingBox();
    const finalSize = new THREE.Vector3;
    geometry.boundingBox.getSize(finalSize);
    const finalCentre = new THREE.Vector3;
    geometry.boundingBox.getCenter(finalCentre);
    const scene = new THREE.Scene;
    currentScene = scene;
    const setting = TERRAIN_COLOURS[selectedColour];
    scene.background = new THREE.Color(setting.background);
    const camera = new THREE.PerspectiveCamera(40, size.width / size.height, .01, 1e3),
      renderer = new THREE.WebGLRenderer({
        antialias: !0
      });
    currentRenderer = renderer, renderer.setPixelRatio(previewPixelRatio()), renderer.setSize(size.width, size.height, !1), "outputEncoding" in renderer && (renderer.outputEncoding = THREE.sRGBEncoding), container.appendChild(renderer.domElement);
    const material = new THREE.MeshPhongMaterial({
        color: setting.model,
        specular: selectedColour === "black" ? 3947580 : 2434341,
        shininess: 3,
        side: THREE.DoubleSide
      }),
      mesh = new THREE.Mesh(geometry, material);
    currentMesh = mesh, scene.add(mesh);
    const hemisphere = new THREE.HemisphereLight(16777215, 3487029, .52);
    scene.add(hemisphere);
    const keyLight = new THREE.DirectionalLight(16777215, .7);
    keyLight.position.set(-7, 11, 8), scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(16777215, .14);
    fillLight.position.set(7, 5, -6), scene.add(fillLight);
    const maxDimension = Math.max(finalSize.x, finalSize.z, 10);
    camera.position.set(0, maxDimension * .9, maxDimension * 1.35), camera.lookAt(finalCentre);
    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = !0, controls.dampingFactor = .07, controls.enablePan = !1, controls.minDistance = 3, controls.maxDistance = 60, controls.target.copy(finalCentre);

    function animate() {
      if (currentRenderer !== renderer) return;
      currentAnimationFrame = requestAnimationFrame(animate);
      if (!previewIsVisible(container)) return;
      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    function resize() {
      if (currentRenderer === renderer) syncRendererSize(renderer, camera, container);
    }
    window.addEventListener("resize", resize);
    afterLayout(resize);
    const resizeTimer = setTimeout(resize, 150);
    currentTerrainCleanup = function() {
      window.removeEventListener("resize", resize);
      clearTimeout(resizeTimer);
      controls.dispose();
    };
  }

  function disposeFramePreview() {
    if (currentFrameAnimationFrame !== null) cancelAnimationFrame(currentFrameAnimationFrame);
    currentFrameAnimationFrame = null;
    if (currentFrameView) {
      if (currentFrameView.cleanup) currentFrameView.cleanup();
      if (currentFrameView.topLabel) currentFrameView.topLabel.remove();
      disposeSceneResources(currentFrameView.scene);
    } else {
      disposeSceneResources(currentFrameAssemblyGroup);
    }
    releasePreviewRenderer(currentFrameRenderer);
    currentFrameView = null;
    currentFrameRenderer = currentFrameTerrainMesh = currentFrameMaterial = currentFrameAssemblyGroup = null;
  }

  function createFramedPreview(container, terrain) {
    if (ensureAllowedFrameColour(terrain), !currentSourceGeometry) throw new Error("Terrain preview is not available yet.");
    const frameKey = JSON.stringify([terrain.size, terrain.orientation, terrain.modelWidthMm, terrain.modelHeightMm, getFrameDimensions(terrain)]);
    // Editing the same terrain moves the existing canvas; no extra mesh or WebGL context.
    if (currentFrameView && currentFrameView.source === currentSourceGeometry && currentFrameView.frameKey === frameKey) {
      if (currentFrameView.container !== container) {
        container.replaceChildren(currentFrameRenderer.domElement, currentFrameView.topLabel);
        currentFrameView.container = container;
      }
      applyTerrainColour(selectedColour);
      applyFrameColour(selectedFrameColour);
      applyMountRotationToCurrentMesh(terrain);
      currentFrameView.terrain = terrain;
      currentFrameView.renderMarker && currentFrameView.renderMarker();
      currentFrameView.renderGpx && currentFrameView.renderGpx();
      currentFrameView.resize();
      return;
    }
    disposeFramePreview(), container.innerHTML = "";
    const viewerSize = getContainerSize(container),
      frame = getFrameDimensions(terrain),
      scene = new THREE.Scene;
    const view = {
      container,
      scene,
      source: currentSourceGeometry,
      frameKey
    };
    currentFrameView = view;
    scene.background = new THREE.Color(12107460);
    const assemblyGroup = new THREE.Group;
    assemblyGroup.userData.ridgeInitialYaw=initialFrameYaw(terrain);
    currentFrameAssemblyGroup = assemblyGroup, scene.add(assemblyGroup);
    const camera = new THREE.PerspectiveCamera(38, viewerSize.width / viewerSize.height, .1, 5e3),
      renderer = new THREE.WebGLRenderer({
        antialias: !0
      });
    currentFrameRenderer = renderer, renderer.setPixelRatio(previewPixelRatio()), renderer.setSize(viewerSize.width, viewerSize.height, !1), "outputEncoding" in renderer && (renderer.outputEncoding = THREE.sRGBEncoding), container.appendChild(renderer.domElement), currentFrameMaterial = new THREE.MeshPhongMaterial({
      color: FRAME_COLOURS[selectedFrameColour].model,
      specular: selectedFrameColour === "black" ? 2368548 : 1579032,
      shininess: selectedFrameColour === "natural" ? 1 : 3
    });
    const backingMaterial = new THREE.MeshPhongMaterial({
        color: 14210510,
        specular: 526344,
        shininess: 1
      }),
      innerWidth = frame.width - 2 * frame.rail,
      innerHeight = frame.height - 2 * frame.rail,
      backing = new THREE.Mesh(new THREE.BoxGeometry(innerWidth, 3, innerHeight), backingMaterial);
    backing.position.y = -1.5, assemblyGroup.add(backing), addFrameBackHardware(assemblyGroup, terrain, frame);
    const frameY = frame.depth / 2;

    function addRail(geometry2, x, y, z) {
      const rail = new THREE.Mesh(geometry2, currentFrameMaterial);
      rail.position.set(x, y, z), assemblyGroup.add(rail)
    }
    const horizontalGeometry = new THREE.BoxGeometry(frame.width, frame.depth, frame.rail),
      verticalGeometry = new THREE.BoxGeometry(frame.rail, frame.depth, innerHeight);
    addRail(horizontalGeometry, 0, frameY, (frame.height - frame.rail) / 2), addRail(horizontalGeometry, 0, frameY, -(frame.height - frame.rail) / 2), addRail(verticalGeometry, -(frame.width - frame.rail) / 2, frameY, 0), addRail(verticalGeometry, (frame.width - frame.rail) / 2, frameY, 0);
    const geometry = currentSourceGeometry.clone();
    geometry.rotateX(-Math.PI / 2), geometry.computeBoundingBox();
    const rawSize = new THREE.Vector3;
    geometry.boundingBox.getSize(rawSize), geometry.scale(Number(terrain.modelWidthMm) / Math.max(rawSize.x, 1e-4), 1, Number(terrain.modelHeightMm) / Math.max(rawSize.z, 1e-4)), geometry.computeBoundingBox();
    const terrainBox = geometry.boundingBox,
      terrainCentre = new THREE.Vector3;
    terrainBox.getCenter(terrainCentre), geometry.translate(-terrainCentre.x, -terrainBox.min.y, -terrainCentre.z), geometry.computeVertexNormals();
    const terrainSetting = TERRAIN_COLOURS[selectedColour];
    currentFrameTerrainMesh = new THREE.Mesh(geometry, new THREE.MeshPhongMaterial({
      color: terrainSetting.model,
      specular: selectedColour === "black" ? 3947580 : 2434341,
      shininess: 3,
      side: THREE.DoubleSide
    })), currentFrameTerrainMesh.position.y = .6, assemblyGroup.add(currentFrameTerrainMesh), applyMountRotationToAssembly(terrain, assemblyGroup, currentFrameTerrainMesh);
    // All 0–10 markers are attached to the terrain mesh and rotate with it.
    // Each is separately sampled against the generated preview's actual surface.
    view.terrain = terrain;
    view.markerGroup = null;
    view.renderMarker = function() {
      const terrain=view.terrain;
      if (view.markerGroup) {
        currentFrameTerrainMesh.remove(view.markerGroup);
        view.markerGroup.traverse(node => {
          if (node.geometry) node.geometry.dispose();
          if (node.material) node.material.dispose();
        });
        view.markerGroup = null;
      }
      const markers=getTerrainMarkers(terrain);
      if (!markers.length) return;
      const group=new THREE.Group();
      currentFrameTerrainMesh.updateMatrixWorld(true);
      for (const marker of markers) {
        const u=Number(marker.u),v=Number(marker.v);
        if (!(u>=.025 && u<=.975 && v>=.025 && v<=.975)) continue;
        const x=(u-.5)*Number(terrain.modelWidthMm);
        const z=(.5-v)*Number(terrain.modelHeightMm);
        const origin=currentFrameTerrainMesh.localToWorld(new THREE.Vector3(x,geometry.boundingBox.max.y+50,z));
        const direction=new THREE.Vector3(0,-1,0).transformDirection(currentFrameTerrainMesh.matrixWorld);
        const hits=new THREE.Raycaster(origin,direction,0,500).intersectObject(currentFrameTerrainMesh,false);
        if (!hits.length) continue;
        const local=currentFrameTerrainMesh.worldToLocal(hits[0].point.clone());
        const height=2.6; // protrudes 2 mm and embeds 0.6 mm
        const cylinder=new THREE.Mesh(
          new THREE.CylinderGeometry(1.5,1.5,height,32,1,false),
          new THREE.MeshPhongMaterial({color:DETAIL_COLOURS[detailColour(marker.colour)].hex,specular:0x333333,shininess:8})
        );
        cylinder.position.set(x,local.y+2-height/2,z);
        group.add(cylinder);
      }
      currentFrameTerrainMesh.add(group);
      view.markerGroup=group;
    };
    view.renderMarker();
    view.gpxLineGroup=null;
    view.renderGpx=function(){
      const terrain=view.terrain;
      if(view.gpxLineGroup) {
        currentFrameTerrainMesh.remove(view.gpxLineGroup);
        view.gpxLineGroup.traverse(node=>{if(node.geometry)node.geometry.dispose();if(node.material)node.material.dispose();});
        view.gpxLineGroup=null;
      }
      if(!currentFrameTerrainMesh || !terrain.gpxSegments || !terrain.gpxSegments.length)return;
      const group=new THREE.Group();
      currentFrameTerrainMesh.updateMatrixWorld(true);
      const maxY=geometry.boundingBox.max.y;
      const ray=new THREE.Raycaster();
      for(const [segmentIndex,segment] of terrain.gpxSegments.entries()){
        const coords=[];
        for(const p of segment){
          const u=Number(p[0]),v=Number(p[1]);
          if(!Number.isFinite(u)||!Number.isFinite(v))continue;
          const x=(u-.5)*Number(terrain.modelWidthMm),z=(.5-v)*Number(terrain.modelHeightMm);
          const origin=currentFrameTerrainMesh.localToWorld(new THREE.Vector3(x,maxY+50,z));
          const dir=new THREE.Vector3(0,-1,0).transformDirection(currentFrameTerrainMesh.matrixWorld);
          ray.set(origin,dir);ray.far=600;
          const hits=ray.intersectObject(currentFrameTerrainMesh,false);
          if(hits.length){const local=currentFrameTerrainMesh.worldToLocal(hits[0].point.clone());coords.push(new THREE.Vector3(x,local.y+.9,z));}
        }
        if(coords.length>=2){
          // 1.0 mm wide, 1.3 mm above the estimated terrain surface;
          // the underside overlaps it by 0.5 mm to prevent visible gaps.
          const top=1.3,bottom=-0.5,halfWidth=0.5;
          const positions=[];
          for(let k=0;k<coords.length-1;k++){
            const a=coords[k],b=coords[k+1];
            const dx=b.x-a.x,dz=b.z-a.z,len=Math.hypot(dx,dz);
            if(len<0.0001)continue;
            const nx=-dz/len*halfWidth,nz=dx/len*halfWidth;
            const subdivisions=Math.min(150,Math.max(1,Math.ceil(len/0.8)));
            for(let j=0;j<subdivisions;j++){
              const c=a.clone().lerp(b,j/subdivisions),d=a.clone().lerp(b,(j+1)/subdivisions);
              const p=[
                [c.x+nx,c.y+top,c.z+nz],[c.x-nx,c.y+top,c.z-nz],
                [d.x-nx,d.y+top,d.z-nz],[d.x+nx,d.y+top,d.z+nz],
                [c.x+nx,c.y+bottom,c.z+nz],[c.x-nx,c.y+bottom,c.z-nz],
                [d.x-nx,d.y+bottom,d.z-nz],[d.x+nx,d.y+bottom,d.z+nz]
              ];
              const faces=[[0,1,2],[0,2,3],[6,5,4],[7,6,4],
                           [4,5,1],[4,1,0],[3,2,6],[3,6,7],
                           [4,0,3],[4,3,7],[1,5,6],[1,6,2]];
              for(const face of faces)for(const idx of face)positions.push(...p[idx]);
            }
          }
          if(positions.length){
            const ribbon=new THREE.BufferGeometry();
            ribbon.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
            ribbon.computeVertexNormals();
            group.add(new THREE.Mesh(ribbon,new THREE.MeshPhongMaterial({color:DETAIL_COLOURS[gpxSegmentColour(terrain,segmentIndex)].hex,side:THREE.DoubleSide})));
          }
        }
      }
      currentFrameTerrainMesh.add(group);
      view.gpxLineGroup=group;
    };
    view.renderGpx();
    const hemisphere = new THREE.HemisphereLight(16777215, 3487029, .52);
    scene.add(hemisphere);
    const keyLight = new THREE.DirectionalLight(16777215, .7);
    keyLight.position.set(-7, 11, 8), scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(16777215, .14);
    fillLight.position.set(7, 5, -6), scene.add(fillLight);
    const maxDimension = Math.max(frame.width, frame.height),
      phoneCameraScale = viewerSize.width <= 480 ? 1.35 : 1;
    camera.position.set(0, maxDimension * .95 * phoneCameraScale, maxDimension * 1.1 * phoneCameraScale);
    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = !0, controls.dampingFactor = .07, controls.enablePan = !1, controls.target.set(0, frame.depth * .15, 0), camera.lookAt(controls.target);
    const topLabel = document.createElement("div");
    view.topLabel = topLabel;
    topLabel.className = "terrain-top-of-frame-label", topLabel.textContent = "TOP OF FRAME", topLabel.setAttribute("aria-label", "Top edge of the finished frame. Preview guide only."), Object.assign(topLabel.style, {
      position: "absolute",
      left: "0",
      top: "0",
      transform: "translate(-50%, -50%)",
      zIndex: "5",
      pointerEvents: "none",
      userSelect: "none",
      whiteSpace: "nowrap",
      padding: "7px 11px",
      borderRadius: "6px",
      background: "rgba(255, 255, 255, 0.96)",
      color: "#253b30",
      border: "1px solid rgba(37, 59, 48, 0.25)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
      fontFamily: "Arial, sans-serif",
      fontSize: "11px",
      fontWeight: "700",
      lineHeight: "1.2",
      letterSpacing: "0.7px",
      visibility: "hidden"
    }), container.appendChild(topLabel), topLabel.style.setProperty("display", "block", "important"), topLabel.style.setProperty("opacity", "1", "important"), topLabel.style.setProperty("z-index", "50", "important");
    const previewSize = normalise(terrain.size),
      previewOrientation = normalise(terrain.orientation);
    let topAnchorX = 0,
      topAnchorZ = (frame.height - frame.rail) / 2;
    previewOrientation === "landscape" && (previewSize === "small" || previewSize === "mini") && (topAnchorX = (frame.width - frame.rail) / 2, topAnchorZ = 0), previewOrientation === "landscape" && previewSize === "medium" && (topAnchorX = -(frame.width - frame.rail) / 2, topAnchorZ = 0);
    const topAnchor = new THREE.Vector3(topAnchorX, frame.depth + 1, topAnchorZ),
      projectedTop = new THREE.Vector3;

    function updateTopLabel() {
      const width = renderer.domElement.clientWidth,
        height = renderer.domElement.clientHeight;
      if (!width || !height) {
        topLabel.style.visibility = "hidden";
        return
      }
      if (topLabel.style.transform = "translate(-50%, -50%)", projectedTop.copy(topAnchor).applyMatrix4(assemblyGroup.matrixWorld).project(camera), !(Number.isFinite(projectedTop.x) && Number.isFinite(projectedTop.y) && Number.isFinite(projectedTop.z) && projectedTop.z >= -1 && projectedTop.z <= 1)) {
        topLabel.style.visibility = "hidden";
        return
      }
      let x = (projectedTop.x * .5 + .5) * width,
        y = (-projectedTop.y * .5 + .5) * height;
      const halfWidth = topLabel.offsetWidth / 2,
        halfHeight = topLabel.offsetHeight / 2,
        edgePadding = 6;
      x = Math.max(halfWidth + edgePadding, Math.min(width - halfWidth - edgePadding, x)), y = Math.max(halfHeight + edgePadding, Math.min(height - halfHeight - edgePadding, y)), topLabel.style.left = x + "px", topLabel.style.top = y + "px", topLabel.style.visibility = "visible"
    }

    function animate() {
      if (currentFrameView !== view || currentFrameRenderer !== renderer) return;
      currentFrameAnimationFrame = requestAnimationFrame(animate);
      if (!previewIsVisible(view.container)) return;
      controls.update();
      renderer.render(scene, camera);
      updateTopLabel();
    }
    animate();

    function resize() {
      if (currentFrameView === view && currentFrameRenderer === renderer) syncRendererSize(renderer, camera, view.container);
    }
    view.resize = resize;
    window.addEventListener("resize", resize);
    afterLayout(resize);
    const resizeTimers = [setTimeout(resize, 150), setTimeout(resize, 500)];
    view.cleanup = function() {
      window.removeEventListener("resize", resize);
      resizeTimers.forEach(clearTimeout);
      controls.dispose();
    };
  }

  // Both the configurator and FAQ call the shared assets/ridge-delivery.js module.
  function renderRidgeDeliveryEstimate(wrapper, section) {
    const box = section.querySelector('[data-ridge-delivery]');
    if (!box) return;
    const country = String(wrapper.dataset.customerCountry || 'NZ').toUpperCase();
    if (!window.RidgeDelivery) {
      console.warn('[Ridge Studios delivery] Shared delivery script was not loaded.');
      box.querySelector('.ridge-delivery-dates').textContent = 'Delivery estimate temporarily unavailable';
      return;
    }
    const estimate = window.RidgeDelivery.getEstimate({
      country,
      productionMin: wrapper.dataset.productionMinDays,
      productionMax: wrapper.dataset.productionMaxDays,
      shippingMin: wrapper.dataset[country.toLowerCase() + 'ShippingMinDays'],
      shippingMax: wrapper.dataset[country.toLowerCase() + 'ShippingMaxDays']
    });
    if (!estimate) { box.hidden = true; return; }
    box.hidden = false;
    box.setAttribute('aria-label', estimate.ariaLabel);
    box.querySelector('.ridge-delivery-country-label').textContent = estimate.heading;
    box.querySelector('.ridge-delivery-dates').textContent = estimate.dates;
    box.querySelector('.ridge-delivery-order').textContent = estimate.order;
    box.querySelector('.ridge-delivery-making').textContent = estimate.making;
    box.querySelector('.ridge-delivery-shipping-label').textContent = estimate.shippingLabel;
    box.querySelector('.ridge-delivery-shipping-days').textContent = estimate.shippingDays;
    box.querySelector('.ridge-delivery-note').textContent = estimate.note;
  }

  function createFrameSection(wrapper) {
    let section = wrapper.querySelector(".terrain-frame-section");
    if (section) return section;
    section = document.createElement("div"), section.className = "terrain-frame-section", section.style.display = "none", section.innerHTML = `

<div class="terrain-section-title">

<h3>
  Your finished terrain
</h3>

<span>
  Check your area, choose your colours and add your finished design to the cart.
</span>

</div>

<div class="terrain-frame-colour-controls">

<span class="terrain-frame-colour-title">
  Wood frame finish
</span>

<div class="terrain-frame-colour-buttons"></div>

</div>

<div class="terrain-mount-orientation-controls">

<span class="terrain-mount-orientation-title">
  Terrain direction
</span>

<div class="terrain-mount-orientation-buttons">

<button
  type="button"
  class="terrain-mount-orientation-button terrain-rotate-model-button"
>
  Rotate Terrain 90\xB0
</button>

</div>

<span class="terrain-mount-orientation-help">
  Each press rotates only the terrain 90\xB0. The frame and hanging hardware stay fixed.
</span>

</div>

<div class="terrain-frame-canvas"></div>

<div class="terrain-viewer-help terrain-frame-viewer-help">

<strong>
  Interactive frame preview
</strong>

<span>
  Click and drag to rotate the frame. Scroll with your mouse wheel, or pinch on mobile, to zoom and inspect the front, sides and rear hanging hardware.
</span>

</div>

<div class="terrain-preview-disclaimer">

<strong>
  Preview note:
</strong>

<span>
  The 3D preview is an indication of the final product. Terrain detail, colour, frame appearance and proportions may vary slightly in the finished physical piece.
</span>

</div>



<div class="terrain-frame-summary">

<div class="terrain-frame-summary-item">

  <span>
    Terrain dimensions
  </span>

  <strong class="terrain-frame-terrain-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Final model height
  </span>

  <strong class="terrain-final-height-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Frame dimensions
  </span>

  <strong class="terrain-frame-size-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Chosen area
  </span>

  <strong class="terrain-chosen-area-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Geographic area
  </span>

  <strong class="terrain-area-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Height multiplier
  </span>

  <strong class="terrain-frame-height-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Model colour
  </span>

  <strong class="terrain-frame-terrain-colour-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Frame finish
  </span>

  <strong class="terrain-frame-colour-summary">
    \u2014
  </strong>

</div>

<div class="terrain-frame-summary-item">

  <span>
    Terrain direction
  </span>

  <strong class="terrain-mount-orientation-summary">
    0\xB0
  </strong>

</div>

</div>
<div class="ridge-delivery-estimate" data-ridge-delivery role="group" aria-label="Estimated New Zealand delivery">
  <div class="ridge-delivery-heading"><span class="ridge-delivery-emoji" aria-hidden="true">🚚</span><span class="ridge-delivery-country-label">Estimated NZ delivery</span></div>
  <p class="ridge-delivery-dates" aria-live="polite">Calculating delivery dates…</p>
  <p class="ridge-delivery-order"></p>
  <div class="ridge-delivery-rows">
    <div class="ridge-delivery-row"><span>Made to order</span><strong class="ridge-delivery-making"></strong></div>
    <div class="ridge-delivery-row"><span class="ridge-delivery-shipping-label">NZ shipping after making</span><strong class="ridge-delivery-shipping-days">2–3 working days</strong></div>
  </div>
  <small class="ridge-delivery-note">Estimates exclude weekends and NZ national public holidays and Auckland Anniversary. Regional destination holidays or courier delays may affect delivery.</small>
</div>
<div class="terrain-frame-actions">

  <button
    type="button"
    class="terrain-frame-back"
  >
    Edit area
  </button>

  <button
    type="button"
    class="terrain-frame-confirm"
  >
    Add to cart
  </button>

</div>
`, wrapper.querySelector(".terrain-preview-section").insertAdjacentElement("afterend", section);
    const buttons = section.querySelector(".terrain-frame-colour-buttons");
    Object.keys(FRAME_COLOURS).forEach(function(key) {
      const setting = FRAME_COLOURS[key],
        button = document.createElement("button");
      button.type = "button", button.className = "terrain-frame-colour-button", button.dataset.frameColour = key, button.innerHTML = '<span class="terrain-frame-colour-swatch" style="background:#' + setting.model.toString(16).padStart(6, "0") + '"></span><span>' + setting.label + "</span>", button.addEventListener("click", function() {
        const terrain = window.selectedTerrain;
        isFrameColourAllowed(terrain, key) && (selectedFrameColour = key, applyFrameColour(key), updateFrameColourButtons(section))
      }), buttons.appendChild(button)
    });
    const rotateTerrainButton = section.querySelector(".terrain-rotate-model-button");
    return rotateTerrainButton && rotateTerrainButton.addEventListener("click", function() {
      const terrain = window.selectedTerrain;
      terrain && (rotateTerrainBy90(terrain), applyMountRotationToCurrentMesh(terrain), updateMountOrientationButtons(section, terrain), updateFrameSummary())
    }), section.querySelector(".terrain-frame-back").addEventListener("click", function(event) {
      event.preventDefault();
      hideFinishedFrame(wrapper);
      wrapper.querySelector(".terrain-preview-section").style.display = "none";
      if (wrapper.dataset.ridgeStudioPage === "true" && window.innerWidth >= 990)
        wrapper.querySelector(".ridge-studio-sidebar")?.scrollTo({top: 0, behavior: "instant"});
      else wrapper.querySelector(".terrain-map-step").scrollIntoView({behavior: "smooth", block: "start"});
    }), section.querySelector(".terrain-frame-confirm").addEventListener("click", async function(event) {
      event.preventDefault();
      const button = this;
      if (button.disabled || section.dataset.saving === "true" || section.dataset.ready !== "true") return;
      const terrain = window.selectedTerrain;
      const errorBox = section.querySelector(".terrain-frame-cart-error");
      section.dataset.saving = "true";
      section.querySelectorAll("button").forEach(function(control) { control.disabled = true; });
      wrapper.dataset.terrainSaving = "true";
      button.textContent = "Saving your terrain…";
      errorBox.hidden = true;
      try {
        terrain.colour = selectedColour;
        terrain.frameColour = selectedFrameColour;
        const frame = getFrameDimensions(terrain);
        terrain.frameWidthMm = frame.width;
        terrain.frameHeightMm = frame.height;
        terrain.frameDepthMm = frame.depth;
        await addTerrainToCart(wrapper, terrain);
        const cartRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
        window.location.href = (cartRoot || "/") + "cart";
      } catch (error) {
        errorBox.textContent = error.message || "We couldn’t add your terrain. Please try again.";
        errorBox.hidden = false;
        section.dataset.saving = "false";
        delete wrapper.dataset.terrainSaving;
        section.querySelectorAll("button").forEach(function(control) { control.disabled = false; });
        syncFrameColourAvailability(section, terrain);
        updateFrameSummary();
        errorBox.textContent = error.message || "We couldn’t add your terrain. Please try again.";
        errorBox.hidden = false;
      }
    }), setupCombinedFrame(section), updateFrameColourButtons(section), renderRidgeDeliveryEstimate(wrapper, section),
      // If the customer leaves the configurator open overnight, recalculate
      // next time they return to the tab as well as once per hour.
      document.addEventListener("visibilitychange", function() {
        if (!document.hidden && section.isConnected) renderRidgeDeliveryEstimate(wrapper, section);
      }),
      setInterval(function() { if (section.isConnected) renderRidgeDeliveryEstimate(wrapper, section); }, 3600000), section
  }

  function setupCombinedFrame(section) {
    createColourControls(section);
    const summary = section.querySelector(".terrain-frame-summary");
    const details = document.createElement("details");
    details.className = "terrain-more-details";
    details.innerHTML = "<summary>More terrain details</summary><div class='terrain-frame-summary'></div>";
    summary.after(details);
    const extra = details.querySelector("div");
    [".terrain-final-height-summary", ".terrain-chosen-area-summary", ".terrain-area-summary",
      ".terrain-frame-height-summary", ".terrain-mount-orientation-summary"].forEach(function(selector) {
      const value = summary.querySelector(selector);
      if (value) extra.appendChild(value.closest(".terrain-frame-summary-item"));
    });
    const price = document.createElement("p");
    price.className = "terrain-frame-price";
    price.setAttribute("aria-live", "polite");
    const error = document.createElement("p");
    error.className = "terrain-frame-cart-error";
    error.setAttribute("role", "alert");
    error.hidden = true;
    const actions = section.querySelector(".terrain-frame-actions");
    actions.before(price, error);
    section.dataset.ready = "false";
  }

  function updateFramePurchase(section, terrain) {
    if (section.dataset.saving === "true") return;
    const button = section.querySelector(".terrain-frame-confirm");
    const price = section.querySelector(".terrain-frame-price");
    const errorBox = section.querySelector(".terrain-frame-cart-error");
    try {
      const variant = resolveSelectedVariant(getWrapper(terrain), terrain);
      if (variant.available === false) throw new Error("This colour and frame combination is unavailable. Please choose another.");
      const addons = resolveTerrainAddonVariants(getWrapper(terrain), terrain);
      const totalCents = Number(variant.price) + addons.reduce((sum, addon) => sum + addon.price, 0);
      const breakdown = addons.map(a => a.label + ' +' + formatMoney(a.price, true));
      if (isTallTerrain(terrain)) breakdown.unshift('Tall terrain +NZ$10 included in base price');
      price.textContent = terrain.size + ' · ' + formatMoney(variant.price, true) +
        (breakdown.length ? ' | ' + breakdown.join(' | ') : '') + ' · Total ' + formatMoney(totalCents, true);
      button.textContent = 'Add to cart — ' + formatMoney(totalCents, false);
      button.disabled = section.dataset.ready !== "true";
      errorBox.hidden = true;
    } catch (error) {
      button.disabled = true;
      button.textContent = "Choose another combination";
      price.textContent = "Price unavailable";
      errorBox.textContent = error.message;
      errorBox.hidden = false;
    }
  }

  function syncFrameColourAvailability(section, terrain) {
    ensureAllowedFrameColour(terrain), section.querySelectorAll(".terrain-frame-colour-button").forEach(function(button) {
      const allowed = isFrameColourAllowed(terrain, button.dataset.frameColour);
      button.style.display = allowed ? "" : "none", button.disabled = !allowed
    }), updateFrameColourButtons(section)
  }

  function updateFrameColourButtons(section) {
    section.querySelectorAll(".terrain-frame-colour-button").forEach(function(button) {
      button.classList.toggle("selected", button.dataset.frameColour === selectedFrameColour)
    })
  }

  function updateMountOrientationButtons(section, terrain) {
    const button = section.querySelector(".terrain-rotate-model-button");
    if (!button) return;
    const rotation = getMountRotationDegrees(terrain);
    button.textContent = "Rotate Terrain 90\xB0", button.title = "Current terrain rotation: " + rotation + "\xB0", button.setAttribute("aria-label", "Rotate terrain 90 degrees. Current rotation " + rotation + " degrees.")
  }

  function applyFrameColour(key) {
    currentFrameMaterial && (currentFrameMaterial.color.setHex(FRAME_COLOURS[key].model), currentFrameMaterial.needsUpdate = !0, updateFrameSummary())
  }

  function updateFrameSummary() {
    const terrain = window.selectedTerrain;
    if (!terrain) return;
    ensureAllowedFrameColour(terrain);
    const section = getWrapper(terrain).querySelector(".terrain-frame-section");
    if (!section) return;
    const frame = getFrameDimensions(terrain);
    section.querySelector(".terrain-frame-size-summary").textContent = frame.width / 10 + " \xD7 " + frame.height / 10 + " cm \xB7 " + frame.depth / 10 + " cm deep", section.querySelector(".terrain-frame-terrain-summary").textContent = Number(terrain.modelWidthMm) / 10 + " \xD7 " + Number(terrain.modelHeightMm) / 10 + " cm", section.querySelector(".terrain-final-height-summary").textContent = formatModelHeight(terrain.finalModelHeightMm), section.querySelector(".terrain-chosen-area-summary").textContent = formatDistance(terrain.width) + " \xD7 " + formatDistance(terrain.height), section.querySelector(".terrain-area-summary").textContent = formatArea(terrain.area), section.querySelector(".terrain-frame-height-summary").textContent = formatMultiplier(terrain.heightMultiplier), section.querySelector(".terrain-frame-terrain-colour-summary").textContent = TERRAIN_COLOURS[selectedColour].label, section.querySelector(".terrain-frame-colour-summary").textContent = FRAME_COLOURS[selectedFrameColour].label;
    const directionOutput = section.querySelector(".terrain-mount-orientation-summary");
    directionOutput && (directionOutput.textContent = getMountRotationLabel(terrain));
    updateFramePurchase(section, terrain)
  }

  function addMarkerAndGpxControls(section,terrain) {
    // Location marker and GPX upload are chosen on the MAP before generation.
    // Detail colours can be edited here without uploading or regenerating terrain.
    let controls=section.querySelector('.ridge-customisation-controls');
    if(!controls) {
      controls=document.createElement('div');
      controls.className='ridge-customisation-controls';
      controls.innerHTML=`<div class="ridge-customisation-block">
        <div class="ridge-customisation-heading"><strong>Selected extras</strong></div>
        <p class="ridge-marker-state"></p><p class="ridge-gpx-state"></p>
        <div class="ridge-detail-colour-list"></div>
        <p class="ridge-customisation-hint">Choose a colour for each trail and marker. To change their positions or files, choose <b>Edit area</b>.</p>
      </div>`;
      section.querySelector('.terrain-frame-canvas').after(controls);
    }
    const markerState=controls.querySelector('.ridge-marker-state');
    const gpxState=controls.querySelector('.ridge-gpx-state');
    if(markerState)markerState.textContent=getTerrainMarkers(terrain).length
      ? getTerrainMarkers(terrain).length+' location marker(s) · +NZ$5 total'
      : 'No location markers';
    if(gpxState)gpxState.textContent=terrain.gpxName?'GPX trail: '+terrain.gpxName+' · +NZ$10':'No GPX trail';
    const list=controls.querySelector('.ridge-detail-colour-list');
    list.replaceChildren();
    const addRow=(name,item,render) => {
      const row=document.createElement('div'); row.className='ridge-detail-colour-row';
      const label=document.createElement('span'); label.className='ridge-detail-colour-name'; label.textContent=name;
      row.append(label,detailColourSelect(item.colour,'Colour for '+name,value => {
        item.colour=value;
        if(currentFrameView && currentFrameView.terrain===terrain) currentFrameView[render]();
        const wrapper=getWrapper(terrain);
        wrapper.dispatchEvent(new CustomEvent('ridge-detail-colours-changed',{detail:terrain}));
      }));
      list.appendChild(row);
    };
    getTerrainMarkers(terrain).forEach((marker,index) => addRow('Marker '+(index+1),marker,'renderMarker'));
    terrain.gpxRoutes=getGpxRoutes(terrain);
    terrain.gpxRoutes.forEach((route,index) => addRow('GPX '+(index+1)+' · '+route.name,route,'renderGpx'));
  }

  function syncStudioFrameCanvas(wrapper, section) {
    const frameCanvas = wrapper.querySelector('.terrain-frame-canvas');
    if (!frameCanvas || wrapper.dataset.ridgeStudioPage !== 'true') return;
    if (window.innerWidth >= 990 && frameCanvas.parentElement !== wrapper) wrapper.appendChild(frameCanvas);
    else if (window.innerWidth < 990 && frameCanvas.parentElement !== section)
      section.querySelector('.terrain-frame-viewer-help').before(frameCanvas);
  }

  function showFramedPreview(terrain) {
    const wrapper = getWrapper(terrain),
      section = createFrameSection(wrapper);
    addMarkerAndGpxControls(section,terrain);
    // The desktop studio sidebar scrolls independently. Keep the WebGL pane
    // outside it so the sidebar cannot clip or shrink the framed preview.
    syncStudioFrameCanvas(wrapper, section);
    if (!wrapper.__ridgeFrameCanvasResizeBound) {
      wrapper.__ridgeFrameCanvasResizeBound = true;
      window.addEventListener('resize', function() { syncStudioFrameCanvas(wrapper, section); });
    }
    const markerState = section.querySelector('.ridge-marker-state');
    if(markerState) markerState.textContent = getTerrainMarkers(terrain).length
      ? getTerrainMarkers(terrain).length+' printed location marker(s) · +NZ$5 total'
      : 'No printed markers selected · NZ$0';
    wrapper.dataset.terrainStage = "frame";
    wrapper.querySelector(".terrain-preview-section").style.display = "none";
    section.dataset.ready = "false";
    updateColourButtons(section.querySelector(".terrain-colour-controls"));
    terrain.mountRotationDegrees === void 0 && setMountRotationDegrees(terrain, 180), updateMountOrientationButtons(section, terrain), syncFrameColourAvailability(section, terrain), section.style.display = "block", updateFrameSummary();
    queueFramePreview(section, ".terrain-frame-canvas", terrain);
    if (wrapper.dataset.ridgeStudioPage === "true" && window.innerWidth >= 990)
      wrapper.querySelector(".ridge-studio-sidebar")?.scrollTo({top: 0, behavior: "instant"});
    else section.scrollIntoView({behavior: "smooth"})
  }

  function queueFramePreview(section, selector, terrain) {
    const request = ++frameViewRequest;
    afterLayout(function() {
      if (request !== frameViewRequest || window.selectedTerrain !== terrain || section.style.display === "none") return;
      const container = section.querySelector(selector) || getWrapper(terrain).querySelector(selector);
      try {
        createFramedPreview(container, terrain);
        section.dataset.ready = "true";
        updateFramePurchase(section, terrain);
      } catch (error) {
        console.error("Terrain frame preview could not be shown:", error);
        disposeFramePreview();
        section.dataset.ready = "error";
        updateFramePurchase(section, terrain);
        const message = document.createElement("p");
        message.setAttribute("role", "alert");
        message.textContent = "The 3D preview could not open. Your terrain selection is still saved on this page.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.textContent = "Try preview again";
        retry.addEventListener("click", function(event) {
          event.preventDefault();
          queueFramePreview(section, selector, terrain);
        });
        container.replaceChildren(message, retry);
        const confirm = getWrapper(terrain).querySelector(".terrain-confirm-preview");
        if (confirm) {
          confirm.disabled = false;
          confirm.textContent = "Use This Terrain";
        }
      }
    });
  }

  function createAssemblyReferenceDataUrls(terrain) {
    if (!currentSourceGeometry) throw new Error("Terrain preview is not available for the assembly reference.");
    ensureAllowedFrameColour(terrain);
    const frame = getFrameDimensions(terrain),
      renderWidth = 1e3,
      renderHeight = 1e3,
      finalHeight = 1110,
      scene = new THREE.Scene;
    scene.background = new THREE.Color(12107460);
    const assemblyGroup = new THREE.Group;
    scene.add(assemblyGroup);
    const renderer = new THREE.WebGLRenderer({
      antialias: !0,
      preserveDrawingBuffer: !0,
      alpha: !1
    });
    try {
      renderer.setPixelRatio(1), renderer.setSize(renderWidth, renderHeight, !1), "outputEncoding" in renderer && (renderer.outputEncoding = THREE.sRGBEncoding);
      const frameMaterial = new THREE.MeshPhongMaterial({
          color: FRAME_COLOURS[selectedFrameColour].model,
          specular: selectedFrameColour === "black" ? 2368548 : 1579032,
          shininess: selectedFrameColour === "natural" ? 1 : 3
        }),
        backingMaterial = new THREE.MeshPhongMaterial({
          color: 14210510,
          specular: 526344,
          shininess: 1,
          transparent: !0,
          opacity: 1
        }),
        innerWidth = frame.width - 2 * frame.rail,
        innerHeight = frame.height - 2 * frame.rail,
        backing = new THREE.Mesh(new THREE.BoxGeometry(innerWidth, 3, innerHeight), backingMaterial);
      backing.position.y = -1.5, assemblyGroup.add(backing), addFrameBackHardware(assemblyGroup, terrain, frame);
      const frameY = frame.depth / 2;

      function addReferenceRail(geometry2, x, y, z) {
        const rail = new THREE.Mesh(geometry2, frameMaterial);
        rail.position.set(x, y, z), assemblyGroup.add(rail)
      }
      const horizontalGeometry = new THREE.BoxGeometry(frame.width, frame.depth, frame.rail),
        verticalGeometry = new THREE.BoxGeometry(frame.rail, frame.depth, innerHeight);
      addReferenceRail(horizontalGeometry, 0, frameY, (frame.height - frame.rail) / 2), addReferenceRail(horizontalGeometry, 0, frameY, -(frame.height - frame.rail) / 2), addReferenceRail(verticalGeometry, -(frame.width - frame.rail) / 2, frameY, 0), addReferenceRail(verticalGeometry, (frame.width - frame.rail) / 2, frameY, 0);
      const geometry = currentSourceGeometry.clone();
      geometry.rotateX(-Math.PI / 2), geometry.computeBoundingBox();
      const rawSize = new THREE.Vector3;
      geometry.boundingBox.getSize(rawSize), geometry.scale(Number(terrain.modelWidthMm) / Math.max(rawSize.x, 1e-4), 1, Number(terrain.modelHeightMm) / Math.max(rawSize.z, 1e-4)), geometry.computeBoundingBox();
      const terrainBox = geometry.boundingBox,
        terrainCentre = new THREE.Vector3;
      terrainBox.getCenter(terrainCentre), geometry.translate(-terrainCentre.x, -terrainBox.min.y, -terrainCentre.z), geometry.computeVertexNormals();
      const terrainSetting = TERRAIN_COLOURS[selectedColour],
        terrainMaterial = new THREE.MeshPhongMaterial({
          color: terrainSetting.model,
          specular: selectedColour === "black" ? 3947580 : 2434341,
          shininess: 3,
          side: THREE.DoubleSide,
          transparent: !0,
          opacity: 1
        }),
        terrainMesh = new THREE.Mesh(geometry, terrainMaterial);
      terrainMesh.position.y = .6, assemblyGroup.add(terrainMesh), applyMountRotationToAssembly(terrain, assemblyGroup, terrainMesh), scene.add(new THREE.HemisphereLight(16777215, 3487029, .62));
      const light = new THREE.DirectionalLight(16777215, .72),
        maxDimension = Math.max(frame.width, frame.height);
      light.position.set(-maxDimension * .35, maxDimension * 2.1, maxDimension * .45), scene.add(light);
      const backLight = new THREE.DirectionalLight(16777215, .52);
      backLight.position.set(maxDimension * .25, -maxDimension * 2.1, maxDimension * .25), scene.add(backLight);
      const aspect = renderWidth / renderHeight,
        paddedWidth = frame.width * 1.16,
        paddedHeight = frame.height * 1.16;
      let viewWidth = paddedWidth,
        viewHeight = paddedHeight;
      viewWidth / viewHeight > aspect ? viewHeight = viewWidth / aspect : viewWidth = viewHeight * aspect;

      function createCamera(fromBack) {
        const camera = new THREE.OrthographicCamera(-viewWidth / 2, viewWidth / 2, viewHeight / 2, -viewHeight / 2, .1, maxDimension * 10);
        return camera.position.set(0, fromBack ? -maxDimension * 3 : maxDimension * 3, 0), camera.up.set(0, 0, 1), camera.lookAt(new THREE.Vector3(0, 0, 0)), camera
      }

      function makeOutputCanvas(heading, footer, drawOrientationArrow) {
        const outputCanvas = document.createElement("canvas");
        outputCanvas.width = renderWidth, outputCanvas.height = finalHeight;
        const context = outputCanvas.getContext("2d");
        if (!context) throw new Error("Could not create the assembly reference image.");
        if (context.fillStyle = "#f5f3ee", context.fillRect(0, 0, renderWidth, finalHeight), context.fillStyle = "#111111", context.textAlign = "center", context.textBaseline = "middle", context.font = "700 28px Arial, sans-serif", context.fillText(heading, renderWidth / 2, 32), context.drawImage(renderer.domElement, 0, 60, renderWidth, renderHeight), drawOrientationArrow) {
          const centreX = renderWidth / 2,
            centreY = 60 + renderHeight / 2,
            arrowLength = 115,
            radians = THREE.MathUtils.degToRad(getMountRotationDegrees(terrain)),
            dx = Math.sin(radians) * arrowLength,
            dy = -Math.cos(radians) * arrowLength,
            endX = centreX + dx,
            endY = centreY + dy;
          context.save(), context.strokeStyle = "#111111", context.fillStyle = "#111111", context.lineWidth = 8, context.lineCap = "round", context.beginPath(), context.moveTo(centreX, centreY), context.lineTo(endX, endY), context.stroke();
          const arrowAngle = Math.atan2(endY - centreY, endX - centreX),
            head = 24;
          context.beginPath(), context.moveTo(endX, endY), context.lineTo(endX - head * Math.cos(arrowAngle - Math.PI / 6), endY - head * Math.sin(arrowAngle - Math.PI / 6)), context.lineTo(endX - head * Math.cos(arrowAngle + Math.PI / 6), endY - head * Math.sin(arrowAngle + Math.PI / 6)), context.closePath(), context.fill(), context.font = "700 22px Arial, sans-serif", context.fillText("MODEL TOP", centreX, centreY + 42), context.restore()
        }
        return context.fillStyle = "#111111", context.textAlign = "center", context.textBaseline = "middle", context.font = "600 20px Arial, sans-serif", context.fillText(footer, renderWidth / 2, finalHeight - 22), outputCanvas
      }
      backingMaterial.opacity = 1, terrainMaterial.opacity = 1, terrainMesh.visible = !0, renderer.render(scene, createCamera(!1));
      const commonFooter = terrain.size + " \xB7 " + TERRAIN_COLOURS[selectedColour].label + " \xB7 " + FRAME_COLOURS[selectedFrameColour].label + " \xB7 terrain rotation " + getMountRotationLabel(terrain),
        birdseyeDataUrl = makeOutputCanvas("TOP / FRONT VIEW \xB7 FRAME TOP \u2191", commonFooter, !0).toDataURL("image/png");
      backingMaterial.opacity = .38, terrainMaterial.opacity = .48, renderer.render(scene, createCamera(!0));
      const backDataUrl = makeOutputCanvas("BOTTOM / BACK VIEW \xB7 FRAME TOP \u2191", commonFooter, !0).toDataURL("image/png");
      return {
        birdseye: birdseyeDataUrl,
        back: backDataUrl
      };
    } finally {
      disposeSceneResources(scene);
      releasePreviewRenderer(renderer);
    }
  }
  async function addTerrainToCart(wrapper, terrain) {
    if (ensureAllowedFrameColour(terrain), !isFrameColourAllowed(terrain, selectedFrameColour)) throw new Error("That frame finish is not available for this size.");
    const variant = resolveSelectedVariant(wrapper, terrain),
      addons = resolveTerrainAddonVariants(wrapper, terrain),
      frame = getFrameDimensions(terrain),
      apiBase = getApiBase(wrapper);
    if (variant.available === false) throw new Error("This combination is unavailable. Please choose another.");
    if (!terrain.jobId) throw new Error("Terrain job ID is missing. Please generate the terrain again.");
    const assemblyReferences = createAssemblyReferenceDataUrls(terrain),
      commitResponse = await fetch(apiBase + "/terrain/commit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          job_id: terrain.jobId,
          size: terrain.size,
          model_colour: TERRAIN_COLOURS[selectedColour].shopifyValue,
          frame_finish: FRAME_COLOURS[selectedFrameColour].shopifyValue,
          orientation: terrain.orientation,
          height_multiplier: Number(terrain.heightMultiplier),
          final_terrain_height_mm: Number.isFinite(Number(terrain.finalModelHeightMm)) ? Number(terrain.finalModelHeightMm) : null,
          model_width_mm: Number.isFinite(Number(terrain.modelWidthMm)) ? Number(terrain.modelWidthMm) : null,
          model_height_mm: Number.isFinite(Number(terrain.modelHeightMm)) ? Number(terrain.modelHeightMm) : null,
          frame_width_mm: frame.width,
          frame_height_mm: frame.height,
          frame_depth_mm: frame.depth,
          selected_width_m: Number.isFinite(Number(terrain.width)) ? Number(terrain.width) : null,
          selected_height_m: Number.isFinite(Number(terrain.height)) ? Number(terrain.height) : null,
          geographic_area_m2: Number.isFinite(Number(terrain.area)) ? Number(terrain.area) : null,
          north: Number(terrain.north),
          south: Number(terrain.south),
          east: Number(terrain.east),
          west: Number(terrain.west),
          shopify_variant_id: Number(variant.id),
          shopify_product_id: Number((isTallTerrain(terrain) ? getTallProductData(wrapper) : getProductData(wrapper)).id),
          assembly_reference_png: assemblyReferences.birdseye,
          assembly_reference_back_png: assemblyReferences.back,
          mount_rotation_degrees: getMountRotationDegrees(terrain),
          rotation_degrees: Number(terrain.rotationDegrees || terrain.rotation_degrees || 0),
          corners: Array.isArray(terrain.corners) && terrain.corners.length === 4 ? terrain.corners : null,
          gpx_routes: getGpxRoutes(terrain),
          markers: getTerrainMarkers(terrain),
          marker: getTerrainMarkers(terrain)[0] || null
        })
      });
    let commitData = {};
    try {
      commitData = await commitResponse.json()
    } catch {
      commitData = {}
    }
    if (!commitResponse.ok) throw new Error(commitData.detail || "Your terrain could not be saved permanently. It has not been added to the cart.");
    if (commitData.status !== "saved") throw new Error("The terrain storage server did not confirm the save. It has not been added to the cart.");
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/",
      properties = {
        "Terrain dimensions": Number(terrain.modelWidthMm) / 10 + " \xD7 " + Number(terrain.modelHeightMm) / 10 + " cm",
        "Final terrain height": formatModelHeight(terrain.finalModelHeightMm),
        "Frame dimensions": frame.width / 10 + " \xD7 " + frame.height / 10 + " cm",
        "Frame depth": frame.depth / 10 + " cm",
        Orientation: formatOrientation(terrain.orientation),
        "Terrain direction": getMountRotationLabel(terrain),
        "Height multiplier": formatMultiplier(terrain.heightMultiplier),
        "Selected area": formatDistance(terrain.width) + " \xD7 " + formatDistance(terrain.height),
        "Geographic area": formatArea(terrain.area),
        "_Terrain Job ID": commitData.job_id,
        "Location markers": getTerrainMarkers(terrain).length
          ? getTerrainMarkers(terrain).length+" markers · NZ$5 total (raised 2 mm)" : "None",
        "GPX trail": terrain.gpxName ? "+NZ$10 " + terrain.gpxName : "None",
        ...detailColourProperties(terrain),
        "Tall terrain surcharge": isTallTerrain(terrain) ? "NZ$10 included in product price" : "Not required",
        "_Mount Rotation Degrees": String(getMountRotationDegrees(terrain)),
        "_Terrain North": Number(terrain.north).toFixed(7),
        "_Terrain South": Number(terrain.south).toFixed(7),
        "_Terrain East": Number(terrain.east).toFixed(7),
        "_Terrain West": Number(terrain.west).toFixed(7)
      },
      response = await fetch(root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          items: [{
            id: Number(variant.id),
            quantity: 1,
            properties
          }, ...addons.map(addon => ({
            id: addon.id,
            quantity: 1,
            properties: {
              '_Ridge Parent Job ID': commitData.job_id,
              '_Ridge Addon Type': addon.key,
              'Terrain customisation': addon.label
            }
          }))]
        })
      }),
      data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || "Shopify could not add this terrain to the cart.");
    return data
  }
  let generationBusy = false;
  let generationVersion = 0;
  function hideFinishedFrame(wrapper) {
    generationVersion++;
    frameViewRequest++;
    const frame = wrapper.querySelector(".terrain-frame-section");
    if (frame) {
      frame.dataset.ready = "false";
      frame.style.display = "none";
    }
    wrapper.dataset.terrainStage = "map";
    disposeFramePreview();
  }

  document.addEventListener("terrain", async function(event) {
    if (!event.detail || generationBusy) return;
    generationBusy = true;
    const terrain = Object.assign({}, event.detail);
    const wrapper = getWrapper(terrain);
    hideFinishedFrame(wrapper);
    const version = generationVersion;
    const previewSection = wrapper.querySelector(".terrain-preview-section");
    const loading = wrapper.querySelector(".terrain-preview-loading");
    const errorBox = wrapper.querySelector(".terrain-preview-error");
    const previewButton = wrapper.querySelector(".terrain-preview-button");
    let retry = wrapper.querySelector(".terrain-generation-retry");
    if (!retry) {
      retry = document.createElement("button");
      retry.type = "button";
      retry.className = "terrain-generation-retry";
      retry.textContent = "Retry preview";
      errorBox.after(retry);
    }
    wrapper.dataset.terrainStage = "loading";
    document.documentElement.classList.add("ridge-preview-busy");
    document.body.classList.add("ridge-preview-busy");
    const lockedPanels = Array.from(wrapper.querySelectorAll(".terrain-size-selection, .terrain-map-step, .terrain-selected"));
    lockedPanels.forEach(function(panel) { panel.inert = true; });
    addStyles();
    previewSection.style.display = "block";
    loading.style.setProperty("display", "flex", "important");
    errorBox.style.setProperty("display", "none", "important");
    if (retry) retry.hidden = true;
    createProgressUI(loading);
    previewButton.disabled = true;
    previewButton.textContent = "Creating your framed preview…";
    try {
      const apiBase = getApiBase(wrapper);
      const requestKey = apiBase + "|" + JSON.stringify(terrainRequestData(terrain));
      let savedJob = pendingTerrainJobs.get(wrapper);
      if (!savedJob || savedJob.key !== requestKey) {
        savedJob = {key: requestKey, jobId: null, result: null};
        pendingTerrainJobs.set(wrapper, savedJob);
      }
      if (!savedJob.jobId) savedJob.jobId = await startTerrainJob(apiBase, terrain);
      const jobId = savedJob.jobId;
      terrain.jobId = jobId;
      const result = savedJob.result || await waitForTerrainJob(apiBase, jobId, loading);
      savedJob.result = result;
      if (version !== generationVersion) return;
      // Printing files are deliberately unavailable until Shopify payment.
      if (result.height_multiplier !== undefined) terrain.heightMultiplier = result.height_multiplier;
      // The generated STL enters the framed view 180° from its upright
      // mounting direction. Start at 180° so the frame AND model are upright,
      // without requiring two manual Rotate 90° clicks. This value also flows
      // into the assembly reference and paid-job mounting metadata.
      setMountRotationDegrees(terrain, 180);
      if (result.preview_model_url !== "/preview-model/" + jobId) {
        const error = new Error("Your preview needs to be regenerated. Please retry.");
        error.forgetTerrainJob = true;
        throw error;
      }
      terrain.previewUrl = apiBase + result.preview_model_url;
      updateProgress(loading, 94, "Loading the 3D viewer...", 10);
      await loadThree();
      const geometry = await loadStlGeometry(terrain.previewUrl, loading, result.preview_size_bytes);
      if (version !== generationVersion) { geometry.dispose(); return; }
      disposeTerrainPreview();
      disposeFramePreview();
      if (currentSourceGeometry && currentSourceGeometry !== geometry) currentSourceGeometry.dispose();
      currentSourceGeometry = geometry;
      geometry.computeBoundingBox();
      const physicalSize = new THREE.Vector3();
      geometry.boundingBox.getSize(physicalSize);
      const fullSize = result.full_model_dimensions_mm || {};
      terrain.finalModelHeightMm = Number(fullSize.z) > 0 ? Number(fullSize.z) : physicalSize.z;
      terrain.generatedWidthMm = Number(fullSize.x) > 0 ? Number(fullSize.x) : physicalSize.x;
      terrain.generatedHeightMm = Number(fullSize.y) > 0 ? Number(fullSize.y) : physicalSize.y;
      // GPX is chosen on the map. Once a prepared DEM exists, upload the
      // full GPX coordinates to the server for geographic reprojection and payment
      // validation. A failed route never silently becomes a paid add-on.
      if(terrain.gpxXml) {
        updateProgress(loading,97,'Positioning the GPX trail on the terrain…',null);
        const response=await fetch(apiBase+'/terrain/gpx/'+encodeURIComponent(jobId),{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({gpx_xml:terrain.gpxXml,routes:terrain.gpxRoutes || []})
        });
        const route=await response.json().catch(()=>({}));
        if(!response.ok || !Array.isArray(route.segments) || !route.segments.length)
          throw new Error(typeof route.detail==='string'?route.detail:'GPX could not be placed on this terrain. Adjust your box and retry.');
        terrain.gpxSegments=route.segments;
        terrain.gpxSegmentRouteIds=route.segment_route_ids || [];
        terrain.gpxRoutes=route.routes || terrain.gpxRoutes || [];
        terrain.gpxName=terrain.gpxNamePending;
        savedJob.gpxUploaded=true;
      } else {
        // Delete only when an earlier GPX was attached to this same preview
        // job. Ordinary previews should never require the GPX endpoint.
        if(savedJob.gpxUploaded) {
          const response=await fetch(apiBase+'/terrain/gpx/'+encodeURIComponent(jobId),{method:'DELETE'});
          if(!response.ok)throw new Error('Could not clear the previous GPX route. Please retry.');
          savedJob.gpxUploaded=false;
        }
        terrain.gpxSegments=[];
        terrain.gpxSegmentRouteIds=[];
        terrain.gpxRoutes=[];
        terrain.gpxName=null;
      }
      terrain.colour = selectedColour;
      window.selectedTerrain = terrain;
      await showFramedPreview(terrain);
      // The frame renderer is scheduled after layout, so wait for its real
      // ready state before dismissing the viewport loading overlay.
      await new Promise(function(resolve, reject) {
        const frame = wrapper.querySelector(".terrain-frame-section");
        if (!frame) { reject(new Error("The framed preview did not open.")); return; }
        if (frame.dataset.ready === "true") { resolve(); return; }
        let settled = false;
        const observer = new MutationObserver(function() {
          if (frame.dataset.ready === "true") finish();
          else if (frame.dataset.ready === "error" || wrapper.querySelector('.terrain-frame-canvas [role="alert"]')) finish(new Error("The framed preview could not be shown."));
        });
        const timer = window.setTimeout(function() { finish(new Error("The framed preview took too long to load.")); }, 60000);
        function finish(error) {
          if (settled) return;
          settled = true;
          observer.disconnect();
          clearTimeout(timer);
          if (error) reject(error); else resolve();
        }
        observer.observe(frame, { attributes:true, attributeFilter:["data-ready"], childList:true, subtree:true });
      });
      loading.style.setProperty("display", "none", "important");
    } catch (error) {
      if (error.forgetTerrainJob) pendingTerrainJobs.delete(wrapper);
      if (version !== generationVersion) return;
      loading.style.setProperty("display", "none", "important");
      errorBox.style.setProperty("display", "block", "important");
      errorBox.textContent = (error.message || "The preview could not load.") + " Your area is still selected. Please try again.";
      if (retry) retry.hidden = false;
      wrapper.dataset.terrainStage = "error";
    } finally {
      loading.style.setProperty("display", "none", "important");
      document.documentElement.classList.remove("ridge-preview-busy");
      document.body.classList.remove("ridge-preview-busy");
      generationBusy = false;
      lockedPanels.forEach(function(panel) { panel.inert = false; });
      previewButton.disabled = false;
      previewButton.textContent = "Generate preview";
    }
  });

  // Invalidate old previews before the map script changes the selected area.
  document.addEventListener("click", function(event) {
    const wrapper = event.target.closest(".terrain-map-wrapper");
    if (!wrapper) return;
    if (wrapper.dataset.terrainSaving === "true" && event.target.closest("button, input, select, .terrain-map")) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    const change = event.target.closest(".terrain-change-selection");
    const mapAction = event.target.closest(".terrain-size-option, .terrain-search-button, .terrain-select-button");
    if (change || mapAction) {
      if (event.target.closest(".terrain-size-carousel-arrow, .terrain-size-carousel-dot, .terrain-image-expand")) return;
      hideFinishedFrame(wrapper);
      wrapper.querySelector(".terrain-preview-section").style.display = "none";
    }
    if (change) {
      event.preventDefault();
      setTimeout(function() {
        if (wrapper.dataset.ridgeStudioPage === "true" && window.innerWidth >= 990)
          wrapper.querySelector(".ridge-studio-sidebar")?.scrollTo({top: 0, behavior: "instant"});
        else wrapper.querySelector(".terrain-map-step").scrollIntoView({behavior: "smooth", block: "start"});
      }, 120);
    }
    if (event.target.closest(".terrain-generation-retry")) {
      event.preventDefault();
      wrapper.querySelector(".terrain-preview-button").click();
    }
  }, true);
  document.addEventListener("keydown", function(event) {
    if (event.key === "Enter" && event.target.matches(".terrain-search-input")) {
      const wrapper = event.target.closest(".terrain-map-wrapper");
      if (wrapper.dataset.terrainSaving === "true") { event.preventDefault(); event.stopImmediatePropagation(); return; }
      hideFinishedFrame(wrapper);
    }
  }, true);

  function initialiseProductData() {
    document.querySelectorAll(".terrain-map-wrapper").forEach(function(wrapper) {
      delete wrapper._ridgeTerrainProductData, updateSizeCardPrices(wrapper)
    })
  }
  initialiseProductData(), document.addEventListener("shopify", function() {
    initialiseProductData()
  })
})(), (function() {
  function addMobileStyles() {
    if (document.getElementById("ridge-terrain-mobile-fixes")) return;
    const style = document.createElement("style");
    style.id = "ridge-terrain-mobile-fixes", style.textContent = `

.terrain-generation-progress {
width: 88% !important;
max-width: 520px !important;
margin-left: auto !important;
margin-right: auto !important;
box-sizing: border-box !important;
}

.terrain-generation-progress-track {
position: relative !important;
display: block !important;
width: 100% !important;
height: 14px !important;
min-height: 14px !important;
overflow: hidden !important;
border-radius: 999px !important;
background: #d2d2d2 !important;
box-shadow:
inset 0 0 0 1px
rgba(0, 0, 0, 0.05) !important;
}

.terrain-generation-progress-bar {
position: absolute !important;
left: 0 !important;
top: 0 !important;
bottom: 0 !important;
display: block !important;
width: 0% !important;
height: 14px !important;
min-height: 14px !important;
border-radius: 999px !important;
background: #222 !important;
transform: none !important;
transition:
width 0.35s ease !important;
z-index: 2 !important;
}

@media screen and (max-width: 989px) {

.terrain-map-wrapper {
width: 100% !important;
max-width: 100% !important;
padding-left: 16px !important;
padding-right: 16px !important;
box-sizing: border-box !important;
}

.terrain-size-options {
grid-template-columns:
repeat(
2,
minmax(0, 1fr)
) !important;
}

}

@media screen and (max-width: 749px) {

html,
body {
max-width: 100% !important;
}

.terrain-map-wrapper {
width: 100% !important;
max-width: 100% !important;
padding:
24px
12px !important;
margin:
0
auto !important;
box-sizing:
border-box !important;
overflow-x:
hidden !important;
}

.terrain-map-header {
margin-bottom:
22px !important;
}

.terrain-map-header h2 {
font-size:
clamp(
26px,
8vw,
36px
) !important;
line-height:
1.08 !important;
}

.terrain-map-header p {
font-size:
14px !important;
line-height:
1.5 !important;
}

.terrain-section-title {
margin-bottom:
14px !important;
}

.terrain-section-title h3 {
font-size:
20px !important;
line-height:
1.2 !important;
}

.terrain-section-title span {
font-size:
14px !important;
line-height:
1.5 !important;
}

.terrain-size-options {
grid-template-columns:
1fr !important;
gap:
10px !important;
}

.terrain-size-option {
width:
100% !important;
min-width:
0 !important;
min-height:
0 !important;
padding:
18px !important;
box-sizing:
border-box !important;
}

.terrain-size-option {
transform:
none !important;
}

.terrain-size-top {
gap:
10px !important;
}

.terrain-size-description {
font-size:
14px !important;
}

.terrain-size-summary {
grid-template-columns:
1fr !important;
gap:
10px !important;
padding:
15px !important;
}

.terrain-orientation-controls,
.terrain-height-controls {
margin-top:
14px !important;
padding:
14px !important;
box-sizing:
border-box !important;
}

.terrain-orientation-buttons {
display:
flex !important;
flex-direction:
row !important;
gap:
8px !important;
}

.terrain-orientation-button {
flex:
1 1 0 !important;
min-width:
0 !important;
min-height:
48px !important;
padding:
10px !important;
}

.terrain-height-slider {
width:
100% !important;
min-height:
38px !important;
}

.terrain-height-description {
font-size:
13px !important;
line-height:
1.45 !important;
}

.terrain-search {
display:
flex !important;
flex-direction:
column !important;
width:
100% !important;
gap:
9px !important;
}

.terrain-search-input {
width:
100% !important;
min-width:
0 !important;
min-height:
50px !important;
font-size:
16px !important;
box-sizing:
border-box !important;
}

.terrain-search-button {
width:
100% !important;
min-width:
0 !important;
min-height:
50px !important;
box-sizing:
border-box !important;
}

.terrain-map {
width:
100% !important;
height:
min(
62vh,
520px
) !important;
min-height:
360px !important;
max-height:
520px !important;
border-radius:
7px !important;
box-sizing:
border-box !important;
}

.terrain-map .leaflet-container {
font-size:
13px !important;
}

.terrain-map .leaflet-control-zoom a {
width:
38px !important;
height:
38px !important;
line-height:
38px !important;
font-size:
20px !important;
}

.terrain-map .leaflet-control-attribution {
font-size:
9px !important;
max-width:
75% !important;
}

.terrain-controls {
width:
100% !important;
margin-top:
12px !important;
}

.terrain-select-button {
width:
100% !important;
min-height:
50px !important;
box-sizing:
border-box !important;
}

.terrain-info {
width:
100% !important;
margin-top:
16px !important;
padding:
18px !important;
box-sizing:
border-box !important;
}

.terrain-empty {
line-height:
1.45 !important;
}

.terrain-stats {
grid-template-columns:
1fr !important;
gap:
8px !important;
}

.terrain-stat {
padding:
14px !important;
}

.terrain-stat strong {
font-size:
18px !important;
}

.terrain-coordinates-details {
width:
100% !important;
}

.terrain-coordinates {
overflow-wrap:
anywhere !important;
word-break:
break-word !important;
box-sizing:
border-box !important;
}

.terrain-preview-button {
width:
100% !important;
min-height:
52px !important;
padding-left:
12px !important;
padding-right:
12px !important;
box-sizing:
border-box !important;
}

.terrain-preview-loading {
width:
100% !important;
min-height:
320px !important;
padding:
18px !important;
box-sizing:
border-box !important;
}

.terrain-generation-progress {
width:
100% !important;
max-width:
none !important;
}

.terrain-generation-progress-title {
font-size:
17px !important;
}

.terrain-generation-progress-status {
min-height:
38px !important;
font-size:
13px !important;
line-height:
1.4 !important;
}

.terrain-generation-progress-bottom {
gap:
8px !important;
font-size:
11px !important;
}

.terrain-preview-canvas {
width:
100% !important;
height:
340px !important;
min-height:
340px !important;
box-sizing:
border-box !important;
}

.terrain-preview-actions {
display:
flex !important;
flex-direction:
column !important;
width:
100% !important;
gap:
9px !important;
}

.terrain-change-selection,
.terrain-confirm-preview {
width:
100% !important;
min-width:
0 !important;
min-height:
50px !important;
box-sizing:
border-box !important;
}

.terrain-colour-controls,
.terrain-frame-colour-controls {
display:
flex !important;
flex-direction:
column !important;
align-items:
stretch !important;
width:
100% !important;
gap:
10px !important;
}

.terrain-colour-buttons,
.terrain-frame-colour-buttons {
width:
100% !important;
display:
grid !important;
grid-template-columns:
repeat(
2,
minmax(0, 1fr)
) !important;
gap:
8px !important;
}

.terrain-colour-button,
.terrain-frame-colour-button {
width:
100% !important;
min-width:
0 !important;
min-height:
48px !important;
padding-left:
8px !important;
padding-right:
8px !important;
box-sizing:
border-box !important;
font-size:
13px !important;
}

.terrain-frame-section,
.terrain-review-section {
width:
100% !important;
box-sizing:
border-box !important;
}

.terrain-frame-summary {
display:
grid !important;
grid-template-columns:
repeat(
2,
minmax(0, 1fr)
) !important;
gap:
8px !important;
}

.terrain-frame-summary-item {
min-width:
0 !important;
padding:
12px !important;
box-sizing:
border-box !important;
}

.terrain-frame-summary-item strong {
font-size:
14px !important;
overflow-wrap:
anywhere !important;
}

.terrain-frame-canvas,
.terrain-review-canvas {
width:
100% !important;
height:
360px !important;
min-height:
360px !important;
max-width:
100% !important;
box-sizing:
border-box !important;
}

.terrain-frame-canvas canvas,
.terrain-review-canvas canvas {
width:
100% !important;
height:
100% !important;
max-width:
none !important;
}

.terrain-frame-actions,
.terrain-review-actions {
display:
flex !important;
flex-direction:
column !important;
width:
100% !important;
gap:
9px !important;
}

.terrain-frame-back,
.terrain-frame-confirm,
.terrain-review-edit,
.terrain-add-to-cart {
width:
100% !important;
min-width:
0 !important;
min-height:
52px !important;
box-sizing:
border-box !important;
}

.terrain-review-layout {
display:
grid !important;
grid-template-columns:
1fr !important;
gap:
16px !important;
width:
100% !important;
}

.terrain-review-card {
width:
100% !important;
padding:
16px !important;
box-sizing:
border-box !important;
}

.terrain-review-product-name {
font-size:
20px !important;
}

.terrain-review-price {
font-size:
19px !important;
}

.terrain-review-row {
width:
100% !important;
gap:
12px !important;
box-sizing:
border-box !important;
}

.terrain-review-row span {
flex:
0 0 40% !important;
}

.terrain-review-row strong {
flex:
1 1 auto !important;
min-width:
0 !important;
max-width:
60% !important;
overflow-wrap:
anywhere !important;
}

}

@media screen and (max-width: 480px) {

.terrain-map-wrapper {
padding-left:
10px !important;
padding-right:
10px !important;
}

.terrain-map {
height:
430px !important;
min-height:
340px !important;
}

.terrain-preview-canvas,
.terrain-frame-canvas,
.terrain-review-canvas {
height:
310px !important;
min-height:
310px !important;
}

.terrain-colour-buttons,
.terrain-frame-colour-buttons {
grid-template-columns:
1fr !important;
}

.terrain-frame-summary {
grid-template-columns:
1fr !important;
}

.terrain-review-row {
align-items:
flex-start !important;
}

}

@media screen and (max-width: 390px) {

.terrain-map {
height:
390px !important;
min-height:
330px !important;
}

.terrain-preview-canvas,
.terrain-frame-canvas,
.terrain-review-canvas {
height:
285px !important;
min-height:
285px !important;
}

.terrain-orientation-buttons {
flex-direction:
column !important;
}

.terrain-review-row {
flex-direction:
column !important;
gap:
4px !important;
}

.terrain-review-row span,
.terrain-review-row strong {
width:
100% !important;
max-width:
100% !important;
flex:
none !important;
text-align:
left !important;
}

}

`, document.head.appendChild(style)
  }

  function syncProgressBars() {
    document.querySelectorAll(".terrain-generation-progress").forEach(function(progress) {
      const bar = progress.querySelector(".terrain-generation-progress-bar"),
        output = progress.querySelector(".terrain-generation-progress-percent"),
        track = progress.querySelector(".terrain-generation-progress-track");
      if (!bar || !output) return;
      const percent = Math.max(0, Math.min(100, parseFloat(output.textContent) || 0));
      bar.style.setProperty("width", percent + "%", "important"), bar.style.setProperty("height", "14px", "important"), bar.style.setProperty("background", "#222", "important"), bar.style.setProperty("transform", "none", "important"), track && (track.setAttribute("role", "progressbar"), track.setAttribute("aria-valuemin", "0"), track.setAttribute("aria-valuemax", "100"), track.setAttribute("aria-valuenow", String(Math.round(percent))))
    })
  }
  const progressObserver = new MutationObserver(function() {
    syncProgressBars()
  });

  function startProgressObserver() {
    document.body && (progressObserver.observe(document.body, {
      childList: !0,
      subtree: !0,
      characterData: !0
    }), syncProgressBars())
  }
  setInterval(function() {
    document.querySelector(".terrain-generation-progress") && syncProgressBars()
  }, 250);
  const enhancedMaps = new WeakSet;

  function enhanceMapForTouch(mapElement) {
    if (!mapElement || enhancedMaps.has(mapElement)) return;
    enhancedMaps.add(mapElement);
    const wrapper = mapElement.closest(".terrain-map-wrapper");
    if (!wrapper) return;
    const selectButton = wrapper.querySelector(".terrain-select-button");
    if (!selectButton) return;

    function drawingIsActive() {
      const buttonText = String(selectButton.textContent || "").trim().toLowerCase(),
        cursor = String(mapElement.style.cursor || "").trim().toLowerCase();
      return buttonText.indexOf("drag over map") !== -1 || cursor === "crosshair"
    }

    function sendMouseEvent(type, touch, buttons) {
      if (!touch) return;
      const mouseEvent = new MouseEvent(type, {
        bubbles: !0,
        cancelable: !0,
        view: window,
        clientX: touch.clientX,
        clientY: touch.clientY,
        screenX: touch.screenX || 0,
        screenY: touch.screenY || 0,
        button: 0,
        buttons
      });
      mapElement.dispatchEvent(mouseEvent)
    }
    mapElement.addEventListener("touchstart", function(event) {
      !drawingIsActive() || event.touches.length !== 1 || (event.preventDefault(), sendMouseEvent("mousedown", event.touches[0], 1))
    }, {
      passive: !1
    }), mapElement.addEventListener("touchmove", function(event) {
      !drawingIsActive() || event.touches.length !== 1 || (event.preventDefault(), sendMouseEvent("mousemove", event.touches[0], 1))
    }, {
      passive: !1
    }), mapElement.addEventListener("touchend", function(event) {
      !drawingIsActive() || !event.changedTouches || !event.changedTouches.length || (event.preventDefault(), sendMouseEvent("mouseup", event.changedTouches[0], 0))
    }, {
      passive: !1
    }), mapElement.addEventListener("touchcancel", function(event) {
      !drawingIsActive() || !event.changedTouches || !event.changedTouches.length || sendMouseEvent("mouseup", event.changedTouches[0], 0)
    }, {
      passive: !1
    })
  }

  function enhanceAllMaps() {
    document.querySelectorAll(".terrain-map").forEach(function(mapElement) {
      enhanceMapForTouch(mapElement)
    })
  }
  const pageObserver = new MutationObserver(function() {
    enhanceAllMaps(), syncProgressBars()
  });

  function initialiseMobileFixes() {
    addMobileStyles(), enhanceAllMaps(), startProgressObserver(), document.body && pageObserver.observe(document.body, {
      childList: !0,
      subtree: !0
    })
  }
  window.addEventListener("orientationchange", function() {
    setTimeout(function() {
      window.dispatchEvent(new Event("resize"))
    }, 250)
  }), window.addEventListener("resize", function() {
    syncProgressBars()
  }), document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", initialiseMobileFixes, {
    once: !0
  }) : initialiseMobileFixes()
})();
